// ─────────────────────────────────────────────────────────────────────────────
// Recognition module — TypeScript DTOs mirroring the backend contracts
// (PMP.SharedContracts.DTOs.Recognition / .Fundamentals).
//
// REGLA DE ORO: los puntos JAMÁS viajan en estos DTOs públicos. El scoring vive en
// RulesEngine/AnalyticsService y nunca es visible a empleados. No agregar campos de puntos aquí.
//
// Los enums del backend se serializan por NOMBRE (JsonStringEnumConverter), por eso aquí se
// modelan como uniones de string literales que coinciden con los nombres del enum en C#.
// ─────────────────────────────────────────────────────────────────────────────

/** Origen de un reconocimiento — enum RecognitionOrigin (serializado por nombre). */
export type RecognitionOrigin =
  | 'Diario'
  | 'Brillamos'
  | 'Personalizada'
  | 'Csv'
  | 'Delegado'
  | 'Sistema';

/** Estado de moderación — enum ModerationStatus (serializado por nombre). */
export type ModerationStatus =
  | 'Publicado'
  | 'Pendiente'
  | 'Resuelto'
  | 'Desestimado';

/** Tipo de campaña — enum CampaignType (serializado por nombre). */
export type CampaignType =
  | 'Fundamentales'
  | 'Brillamos'
  | 'Personalizada';

/** Ciclo de vida de una campaña — enum CampaignStatus (serializado por nombre). */
export type CampaignStatus =
  | 'Borrador'
  | 'Activa'
  | 'Finalizada';

/** Diccionario multiidioma (languageCode → texto). */
export type LocalizedText = Record<string, string>;

/**
 * Reconocimiento tal como se expone en el feed y consultas públicas.
 * SIN PUNTOS por diseño (ver regla de oro arriba).
 * Espejo de PMP.SharedContracts.DTOs.Recognition.RecognitionDto.
 */
export interface RecognitionDto {
  id: string;
  /** Emisor. `null` en el feed cuando es anónimo (el historial admin siempre lo registra). */
  emisorId: string | null;
  /** Nombre del emisor para mostrar. `null` si anónimo visto desde el feed. */
  emisorNombre: string | null;
  /** Receptores (multi-destinatario; Europa fuerza 1). */
  receptorIds: string[];
  /** Nombres de los receptores para mostrar. */
  receptorNombres: string[];
  /** Fundamentales seleccionados (default máx 2, Europa 1). */
  fundamentalIds: string[];
  /** Actitudes/Comportamientos seleccionados (solo aplica a F01). */
  behaviorIds: string[];
  /** Daily habits seleccionados. */
  habitIds: string[];
  /** Mensaje del reconocimiento (15–3000 chars; el tope lo fija `MENSAJE_MAX_LENGTH`). */
  mensaje: string;
  /** Fecha (ISO 8601 / DateTimeOffset). */
  fecha: string;
  origen: RecognitionOrigin;
  estadoModeracion: ModerationStatus;
  anonimo: boolean;
  /** Campaña asociada. `null` = reconocimiento diario. */
  campanaId: string | null;
  /** Campaña secundaria cuando fue promovido diario→Brillamos (doble etiqueta). */
  campanaSecundariaId: string | null;
  /** Etiqueta grupal cuando es multi-destinatario. */
  etiquetaGrupal: string | null;
  /**
   * RF-084 — ¿se puede reportar ESTE reconocimiento? Valor efectivo del toggle de moderación,
   * resuelto en servidor sobre la geografía del contenido (Site → BU → OU → ancestros → global).
   * Un DTO anterior a RF-084 no lo trae: se asume permitido y el backend revalida igual.
   */
  reportEnabled?: boolean;

  /** ¿Se puede reaccionar a ESTE reconocimiento? Resuelto en servidor: toggle global de Gestión
   *  Y (si pertenece a una campaña) el toggle de esa campaña. Un DTO anterior a este cambio no lo
   *  trae: se asume permitido, igual que `reportEnabled`. */
  reactionsEnabled?: boolean;

  /** ¿Se muestra de qué campaña proviene este reconocimiento (etiqueta «Recibido en»)? Resuelto en
   *  servidor desde `Campaña.visibilidad.mostrarEtiquetaCampana`. Un DTO anterior a este cambio no
   *  lo trae: se asume que sí, igual que `reportEnabled`. */
  mostrarEtiquetaCampana?: boolean;

  /**
   * WP-A5 · RN-P09 — puntos que vale **ESTA** nominación, o `null`/ausente si no procede o no puede
   * verse. **Jamás el acumulado de nadie** (RN-P12): el acumulado no se expone a su titular por
   * ningún endpoint.
   *
   * 🔴 `null` y `0` NO son lo mismo. `0` es un valor real —una nominación puede valer cero— y `null`
   * es «no procede o no se puede ver». Al pintarlo hay que comprobar `!= null`, nunca la veracidad
   * del número: `if (puntos)` esconde el cero legítimo.
   *
   * 🔴 **No lo deduzcas ni lo recalcules.** Lo decide el backend contra cinco puertas —config de
   * puntos de la OU, toggle de la campaña, que no sea diario, el interruptor «Visible para
   * empleados» y el suelo duro de GDPR— y viaja ya resuelto justamente para que el cliente no tenga
   * que saberlas. Si viene, se enseña; si no, no.
   *
   * ⚠️ Los DTOs que devuelven CREAR, promover o actualizar un reconocimiento lo traen siempre
   * ausente, **y es correcto**: el asiento de puntos lo escribe un consumidor asíncrono y en ese
   * instante todavía no existe. Solo el muro y el detalle pueden servirlo.
   */
  puntosDeEstaNominacion?: number | null;

  /** Etiquetas libres para filtros/reportería. */
  etiquetas: string[] | null;
}

/**
 * Snapshot organizativo de una persona al momento del reconocimiento (histórico 3 años).
 * Espejo de OrgSnapshotDto.
 */
export interface OrgSnapshotDto {
  userId: string;
  displayName: string;
  ouId: string | null;
  ouName: string | null;
  buId: string | null;
  buName: string | null;
  siteId: string | null;
  siteName: string | null;
  jobTitle: string | null;
  area: string | null;
  country: string | null;
  isGdpr: boolean;
}

/** Detalle de un reconocimiento con snapshots. Espejo de RecognitionDetailDto. */
export interface RecognitionDetailDto {
  recognition: RecognitionDto;
  /** T4 — null cuando el reconocimiento es anónimo y quien mira no es admin/moderador. */
  emisorSnapshot: OrgSnapshotDto | null;
  receptorSnapshots: OrgSnapshotDto[];
  reportCount: number;
}

/** Toggles de visibilidad de una campaña (paso 5 del wizard). Espejo de CampaignVisibilityDto. */
export interface CampaignVisibilityDto {
  mostrarRemitente: boolean;
  mostrarFundamental: boolean;
  mostrarTexto: boolean;
  mostrarHabito: boolean;
  enviados: boolean;
  recibidos: boolean;
  anonimos: boolean;
  /** Override SOLO de esta campaña sobre `GestionSettingsDto.permitirAnonimos`. `null`/ausente = hereda. */
  permitirAnonimos?: boolean | null;
  /** Modo de entrada: "app" | "csv" | "ambos". */
  entryMode: string;
  cargaMasiva: boolean;
  /** Si la campaña otorga puntos (scoring). Solo Europa; el frontend nunca muestra los puntos. */
  aplicanPuntos: boolean;
  /** Habilita reaccionar a los post-its de ESTA campaña — independiente del toggle global de
   *  Gestión (`GestionSettingsDto.reactionsEnabled`); cualquiera de los dos en `false` apaga la
   *  reacción (resuelto en servidor, ver `RecognitionDto.reactionsEnabled`). */
  reaccionesHabilitadas: boolean;

  // ── Los 4 toggles del Paso 5 que la maqueta pintaba y el contrato no tenía (B8-3) ──
  /** «Mostrar etiqueta de campaña» — la etiqueta «Recibido en» del post-it. El servidor lo resuelve
   *  y lo manda ya decidido en `RecognitionDto.mostrarEtiquetaCampana`. */
  mostrarEtiquetaCampana: boolean;
  /** «Permitir enviar desde Fundamentales» — si esta campaña acepta ser el destino del check
   *  «enviar también a Brillamos» (T5). */
  permitirDesdeFundamentales: boolean;
  /** «Desactivar envío» — pausa los envíos SIN dar la campaña de baja. */
  envioDesactivado: boolean;
}

/**
 * Campaña de reconocimiento (Brillamos Juntos o personalizada).
 * Espejo de CampaignDto.
 */
export interface CampaignDto {
  id: string;
  /** Nombre por idioma (7 idiomas). */
  nombre: LocalizedText;
  /** Descripción por idioma. */
  descripcion: LocalizedText | null;
  tipo: CampaignType;
  estado: CampaignStatus;
  /** Fecha de inicio (ISO). `null` para Fundamentales default. */
  fechaInicio: string | null;
  /** Fecha de fin (ISO). */
  fechaFin: string | null;
  /** IANA timezone que rige el vencimiento. */
  zonaHoraria: string;
  /** OUs de alcance. */
  alcance: string[];
  /** OUs/usuarios excluidos. */
  exclusiones: string[];
  visibilidad: CampaignVisibilityDto;
  /** Config de diseño de la landing (JSON libre). */
  diseno: LocalizedText | null;
  /**
   * Banner "Brillamos Juntos" PROPIO de esta campaña (subtítulo por idioma) — override del banner
   * global/de programa para el alcance de ESTA campaña. Mismo shape que
   * `CampaignExceptionOverrideDto.bannerSubtitle/bannerLogoUrl` (Tarea 14, "Campañas por OU").
   * `null` = no configurado, hereda el banner global/de programa. No aplica a campañas Brillamos
   * (identidad inmutable, REQ-034).
   */
  bannerSubtitle?: Record<string, string> | null;
  /** Logo del banner por idioma (data-URI o URL), propio de esta campaña. */
  bannerLogoUrl?: Record<string, string> | null;
  /** Campaña padre cuando es variante local. `null` = campaña raíz. */
  parentCampaignId: string | null;
  /** Insignia otorgada al ganador (opcional). */
  insigniaGanadorId: string | null;
  /** Slug del deep-link de la landing. */
  landingSlug: string | null;
  createdBy: string | null;
  createdAt: string | null;
  /**
   * RF-037 — Mínimo de envíos calificados que ESTA campaña exige para sumar racha en el período
   * (1–20). `null` = la campaña no define mínimo propio y sus usuarios usan el global de Gestión.
   */
  streakWeeklyMinimum: number | null;
  /**
   * Tope de envíos propio de la campaña (1–1000). `null` = la campaña no define tope propio y sus
   * usuarios usan el global de Gestión (GestionSettingsDto.maxRecognitionsPerPeriod).
   */
  maxRecognitionsPerPeriod: number | null;
  /**
   * Bug UAT #18 — «No aplica»: la campaña quedó SIN tope de envíos, sin importar el global de Gestión.
   * Tercer estado del tope, aparte de «número propio» y «hereda global» (maxRecognitionsPerPeriod null).
   * Aditivo: las campañas previas se leen como `false`.
   */
  recognitionsLimitDisabled?: boolean;
  /**
   * Bug UAT #18 — Número propio de «veces que puedes reconocer el mismo fundamental a una persona»
   * en el período. `null` = hereda el global de Gestión. Override por campaña, mismo patrón que
   * `maxRecognitionsPerPeriod`.
   */
  maxSameFundamentalPerPerson?: number | null;
  /**
   * Bug UAT #18 — «No aplica» del mismo-fundamental: `true` = sin límite (ignora el global). En Europa
   * el piso GDPR se mantiene (el backend lo respeta). Aditivo: campañas previas se leen `false`.
   */
  sameFundamentalLimitDisabled?: boolean;
  /** "Horarios y Fechas por País" (Brillamos): ventana fecha/horario diario por país. null/vacío = solo global. */
  horariosPorPais?: CampaignCountryScheduleDto[] | null;
  /**
   * RF-130 — Fecha de release del Wrapped para el alcance de esta campaña ("release por geografía
   * con fecha elegida"). Se interpreta en `zonaHoraria`, igual que `fechaInicio`/`fechaFin`.
   * `null` = sin fecha propia: el Wrapped se libera al pasar la campaña a Finalizada.
   *
   * Quien decide con esto es el backend (`GET v1/recap/availability`), no el cliente: aquí viaja
   * para que el asistente de Campañas pueda editarlo.
   */
  wrappedReleaseAt?: string | null;

  /**
   * C2/P17 — cada cuánto la campaña PIDE participación («Mensual», «Cada trimestre», «Cada 3
   * reuniones», «Por evento»). DECLARATIVA: se registra y se reporta, no dispara nada. No es la
   * vigencia — son dos preguntas distintas.
   */
  frecuencia?: string | null;

  /**
   * C2/P18-bis — cuándo se finalizó por el BOTÓN DE PÁNICO. `null` = no se cerró así.
   * Es lo que decide si la pantalla puede ofrecer «deshacer»: una campaña vencida por FECHA no lo
   * trae y sigue siendo terminal.
   */
  finalizadaPorPanicoAt?: string | null;

  /**
   * C2/P19 — meta de PARTICIPANTES de la campaña, para la columna «% de meta» de Históricos.
   * `null` = sin meta, y la columna pinta «—»: un 0 % se leería como «no participó nadie» cuando
   * significa «nadie puso un objetivo».
   */
  metaParticipantes?: number | null;
}

/**
 * Fila de la tabla "Horarios y Fechas por País" de Brillamos. `ouId` = país (OU top-level). Fuera de la
 * ventana de fecha o del horario diario `[horaInicio,horaFin]` (hora de pared en `zonaHoraria`) no se
 * pueden enviar reconocimientos de esa campaña para el país. Espejo de CampaignCountryScheduleDto.
 */
export interface CampaignCountryScheduleDto {
  ouId: string;
  fechaInicio?: string | null;
  fechaFin?: string | null;
  horaInicio?: string | null; // "HH:mm"
  horaFin?: string | null;    // "HH:mm"
  zonaHoraria: string;
  /**
   * ¿La campaña está activa EN ESTE PAÍS? `false` es un kill-switch: la campaña no está disponible ahí
   * ni se puede enviar, sin importar la ventana configurada.
   */
  activo: boolean;
  /**
   * ¿Se aplica la ventana de fecha/hora de ESTA fila? `false` la deja guardada pero SIN efecto, así que
   * el país queda sin restricción horaria. Separado de `activo` tras UAT del 21-ago-2026: un solo
   * interruptor tenía que significar «cierra el país» y «no apliques esta ventana» a la vez.
   * Aditivo — las filas guardadas antes del campo se leen como `true`, que es lo que hacían.
   */
  aplicarHorario?: boolean;
}

/** Escala de comportamiento observable (0-4). Espejo de BehaviorDescriptorDto. */
export interface BehaviorDescriptorDto {
  score: number;
  descriptions: LocalizedText;
}

/** Daily Habit del marco cultural. Espejo de DailyHabitDto. */
export interface DailyHabitDto {
  id: string;
  fundamentalId: string;
  code: string;
  order: number;
  names: LocalizedText;
  questionIntro: LocalizedText;
  /** No se consume en ningún componente hoy; opcional para permitir que el catálogo de
   *  SurveyService (que no lo trae) sea estructuralmente compatible con este tipo. */
  behaviors?: BehaviorDescriptorDto[];
  isActive: boolean;
  attitudeBehaviorId?: string | null;
  image?: string | null;
}

/** Actitud/Comportamiento (nivel intermedio opcional). Espejo de AttitudeBehaviorDto. */
export interface AttitudeBehaviorDto {
  id: string;
  fundamentalId: string;
  code: string;
  order: number;
  names: LocalizedText;
  isActive: boolean;
  image?: string | null;
  descriptions?: LocalizedText | null;
}

/**
 * Fundamental del marco cultural de Sigma (catálogo de Reconocimiento).
 * Espejo de PMP.SharedContracts.DTOs.Fundamentals.FundamentalDto.
 */
export interface FundamentalDto {
  id: string;
  /** Código único estable (ej: "F01"). */
  code: string;
  order: number;
  names: LocalizedText;
  dailyHabits: DailyHabitDto[];
  isActive: boolean;
  color?: string | null;
  shortName?: string | null;
  description?: string | null;
  url?: string | null;
  icon?: string | null;
  image?: string | null;
  shortNames?: LocalizedText | null;
  descriptions?: LocalizedText | null;
  attitudes?: AttitudeBehaviorDto[] | null;
  /** [REQ-092] Participa en el flujo de campañas Brillamos Juntos. */
  enBrillamos?: boolean;
  /** [REQ-093] Id de campaña a la que queda ligado. null = catálogo global. */
  campaignScope?: string | null;
  /** [REQ-095] URL de micrositio del Fundamental. */
  micrositeUrl?: string | null;
}

/** Resultado paginado genérico. Espejo de PagedResultDto<T>. */
export interface PagedResultDto<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
}

// ─── Request bodies ──────────────────────────────────────────────────────────

/** Cuerpo de POST v1/recognitions. Espejo de CreateRecognitionBody. SIN puntos. */
export interface CreateRecognitionBody {
  receptorIds: string[];
  fundamentalIds: string[];
  mensaje: string;
  behaviorIds?: string[];
  habitIds?: string[];
  origen?: RecognitionOrigin;
  anonimo?: boolean;
  campanaId?: string | null;
  etiquetaGrupal?: string | null;
  etiquetas?: string[] | null;
  onBehalfOfUserId?: string | null;
  /** T5 — check "enviar también a Brillamos": reutiliza el diario en la próxima campaña Brillamos. */
  sendToBrillamos?: boolean;
}

/** Filtros del feed GET v1/recognitions. */
export interface RecognitionFeedParams {
  ou?: string;
  campaignId?: string;
  receptorId?: string;
  /**
   * Filtra por emisor (server-side). Se usa para "Enviados" del PROPIO usuario. Regla de negocio
   * (REQ-120): el frontend nunca lo usa con el id de un tercero — el par no ve enviados de otro.
   */
  emisorId?: string;
  /**
   * Filtra por programa del reconocimiento (server-side). `Fundamentales` excluye Brillamos/
   * Personalizada; `Brillamos` exige alguno de esos dos orígenes o promoción a esa campaña
   * (`campanaSecundariaId`). Mismo criterio que `isBrillamosOrigin` (postitShared.tsx), aplicado
   * aquí como filtro y no solo como indicador visual del pin ⭐.
   */
  programa?: 'Fundamentales' | 'Brillamos';
  page?: number;
  pageSize?: number;
  /**
   * RF-130 (H-13) — ventana temporal (ISO, ambos inclusive) sobre la fecha del reconocimiento. La usa
   * el overlay del Recap para acotar el feed al año de la edición: sin esto, el slide `mensaje` de un
   * año ya cerrado se perdía por paginación cuando el titular acumulaba más de 100 reconocimientos
   * recibidos DESPUÉS de ese año. `undefined`/`undefined` es el comportamiento de siempre.
   */
  desde?: string;
  hasta?: string;
  /**
   * 2026-08-24 — "solo vigentes": acota a campañas Brillamos ACTIVAS ahora mismo, en cualquier OU
   * del tenant, en vez de exigir el `campaignId` exacto del que consulta. Dos personas con campañas
   * Brillamos DISTINTAS (misma tenant, distinta OU) pero ambas vigentes deben seguir contando entre
   * sí. Solo tiene efecto junto con `programa: 'Brillamos'`; se ignora con cualquier otro valor.
   */
  soloVigentes?: boolean;
}

/** Motivo de un reporte de moderación. Espejo de ReportBody. */
export interface ReportRecognitionBody {
  motivo: string;
}

/**
 * Cuerpo de PATCH v1/recognitions/{id} (edición post-envío, REQ-107). Solo campos editables;
 * disponible únicamente si la config de OU/campaña habilita la edición. Espejo de UpdateRecognitionBody.
 */
export interface UpdateRecognitionBody {
  mensaje?: string;
  fundamentalIds?: string[];
  behaviorIds?: string[];
  habitIds?: string[];
}

/** Cuerpo de POST v1/recognitions/{id}/promote (diario → Brillamos). Espejo de PromoteBody. */
export interface PromoteRecognitionBody {
  campanaId: string;
}

/** Resumen de reacciones de un reconocimiento (GET/POST v1/recognitions/{id}/reactions). */
export interface ReactionSummaryDto {
  /** Cantidad de reacciones por emoji (clave = emoji, valor = cantidad de usuarios). */
  counts: Record<string, number>;
  /** Suma de todas las reacciones (cuenta usuarios, no tipos de emoji distintos). */
  total: number;
  /** Emoji con el que reaccionó el usuario autenticado, o `null` si no reaccionó. */
  myReaction: string | null;
}

/** Cuerpo de POST v1/recognitions/{id}/reactions — toggle server-side. */
export interface ToggleReactionBody {
  emoji: string;
}

/** Una persona que reaccionó (GET v1/recognitions/{id}/reactions/users). Espejo de `ReactionUserDto`. */
export interface ReactionUserDto {
  userId: string;
  /** Nombre resuelto contra el directorio; cae al `userId` si no se pudo resolver. */
  displayName: string;
  emoji: string;
}

/**
 * Lista de quiénes reaccionaron a un reconocimiento. Se pide bajo demanda al abrir «quién
 * reaccionó», no por cada tarjeta del muro (eso lo cubre `ReactionSummaryDto`).
 */
export interface ReactionUsersDto {
  users: ReactionUserDto[];
}

// ─── Documentos de campaña (WP-12 REQ-112) ─────────────────────────────────────

/**
 * Documento adjunto a una campaña (instructivos, bases legales, privacidad…).
 * Espejo de PMP.SharedContracts.DTOs.Recognition.CampaignDocumentDto
 * (GET v1/campaigns/{id}/documents). SIN puntos.
 */
export interface CampaignDocumentDto {
  id: string;
  /** OJO: el backend serializa `CampaignId`/`LanguageCode`, no `campanaId`/`idioma` como el resto
   * de DTOs de reconocimiento. Los nombres de aquí siguen al backend, que es quien manda el JSON. */
  campaignId: string;
  /** Categoría libre: "howto" | "awards" | "legal" | "privacy" | "cookies" | "notice" | … */
  categoria: string;
  /** Código de idioma del documento (es/en/de/pt/nl/fr/ro). Define la pestaña. */
  languageCode: string;
  /** Título por idioma para el encabezado del visor. */
  titulo: LocalizedText | null;
  /** URL del PDF (deep-link o ruta estática servida por el backend). */
  url: string;
  /** Nombre de archivo sugerido para la descarga. */
  fileName: string | null;
  /** Orden de carga — determina el orden de las pestañas por idioma (REQ-112). */
  orden: number;
}

// ─── Wrapped / resumen anual (WP-13 REQ-087/121 — v1/analytics/recognition/wrapped) ─────────

/**
 * "Wrapped" personal del usuario (GET v1/analytics/recognition/wrapped). Datos REALES sobre la
 * proyección por-usuario del AnalyticsService. SIN puntos (regla de oro). Espejo EXACTO de
 * PMP.SharedContracts.DTOs.Recognition.WrappedDto (serializado camelCase).
 *
 * NOTA de contrato: `rachaMasLarga` NO viaja aquí (sale de StreakDto.record, que el perfil ya
 * consulta). `totales` es un diccionario cuyas claves canónicas son "Enviados" y "Recibidos".
 */
export interface WrappedDto {
  userId: string;
  /** Totales del periodo. Claves canónicas: "Enviados", "Recibidos". Nunca puntos. */
  totales: Record<string, number>;
  /** Id del Fundamental más usado por el usuario (resolver el nombre vía catálogo). */
  topFundamentalId: string | null;
  /** Id de la persona a la que más reconoció. */
  topReceptorId: string | null;
  /** Id de la persona que más lo reconoció (fan #1). */
  topEmisorId: string | null;
  /** Reconocimientos cross-OU (colaboración cruzada). */
  crossOu: number;
  /** Id del mejor mensaje (por reacciones/longitud, GAP-007). */
  mejorMensajeId?: string | null;
  /**
   * Clave ESTABLE de generosidad calculada por Analytics — no texto. Valores:
   * `'generoso'` (≥5 envíos) · `'muyGeneroso'` (≥20) · `'superGeneroso'` (≥50) · `null` (<5). El
   * cliente la traduce contra `wrapped.generosityTag.*`; una clave desconocida no se pinta (RF-130 F1).
   */
  etiquetaGenerosidad?: string | null;
  /**
   * RF-130 — Año calendario natural (1 ene – 31 dic) que cubren estas cifras. `0` = el productor no
   * lo informó (agregado anterior a RF-130); el cliente NO debe inferirlo.
   */
  anio?: number;
  /**
   * RF-130 — Mes de ingreso (1-12) cuando el titular entró DENTRO de `anio` y su resumen no cubre el
   * año completo. `null`/ausente = cubre el año entero, o el directorio no conoce su fecha de ingreso.
   */
  desdeMes?: number | null;
  /**
   * Bug Sesión #80 — Id del Fundamental MENOS usado por el usuario (mismo criterio de "usado" que
   * `topFundamentalId`: ENVIADOS, no recibidos). A diferencia del top, sí compara contra el catálogo
   * activo completo — un Fundamental nunca usado puede ganar con conteo 0, es justo el caso que hace
   * de "tu reto" un llamado a la acción real. `null` si el titular no envió nada en el año.
   */
  bottomFundamentalId?: string | null;
}

/**
 * RF-130 — ¿Puede este colaborador ver su Wrapped, y de qué año? (GET v1/recap/availability).
 * Espejo EXACTO de PMP.SharedContracts.DTOs.Recognition.WrappedAvailabilityDto (camelCase).
 *
 * Es la decisión COMPLETA tomada server-side (Gestión → campaña Brillamos de su OU → edición del
 * Recap → fecha de ingreso): el cliente no vuelve a razonar sobre campañas ni fechas. Sustituye al
 * gate `!!wrapped.data`, que era constante `true` porque el handler del Wrapped siempre devuelve un
 * DTO (con ceros si no hay agregado) — ésa era la causa raíz del criterio 4 del RF.
 */
export interface WrappedAvailabilityDto {
  /** `false` ⇒ el cliente no monta NADA del Wrapped (ni botón, ni teaser, ni overlay). */
  disponible: boolean;
  /** Año calendario que cubre el Wrapped disponible. Es el año que hay que pedirle a Analytics. */
  anio: number;
  /** 1-12 si el titular ingresó dentro de `anio`; null si cubre el año entero (o no se conoce). */
  desdeMes: number | null;
  /** Ingreso muy tardío ⇒ SOLO la tarjeta genérica, sin slides de datos. */
  soloGenerico: boolean;
  /** Instante en que quedó liberado para esta OU. null si no hay fecha fijada — nunca inventada. */
  liberadoEn: string | null;
  /**
   * Clave ESTABLE de diagnóstico (ver `WRAPPED_AVAILABILITY_REASONS`). NO es un texto para el
   * usuario: el frontend jamás la pinta cruda.
   */
  motivo: string;
}

/** Claves estables de `WrappedAvailabilityDto.motivo` — espejo de `WrappedAvailabilityReasons`. */
export const WRAPPED_AVAILABILITY_REASONS = {
  disponible: 'disponible',
  apagadoPorGestion: 'apagado-por-gestion',
  campanaActiva: 'campaña-activa',
  sinEdicionRecap: 'sin-edicion-recap',
  fueraDeVentana: 'fuera-de-ventana',
  fueraDeAlcance: 'fuera-de-alcance',
} as const;

// ─── Rachas / insignias (WP-11 REQ-103: refresco de racha tras enviar) ─────────

/**
 * Insignia. Espejo EXACTO de PMP.SharedContracts.DTOs.Recognition.BadgeDto — el mismo shape que
 * usa `AdminBadgeDto` para la Gestión, porque el backend serializa un solo record para las dos
 * superficies. Antes este tipo declaraba `names`/`icon`, campos que el backend NUNCA emite: contra
 * datos reales el nombre de la insignia salía vacío y caía al código.
 */
export interface BadgeDto {
  id: string;
  code?: string | null;
  /** Nombre por idioma (configurable desde Gestión — RF-038). */
  nombre?: LocalizedText | null;
  descripcion?: LocalizedText | null;
  /** Semanas de récord necesarias para desbloquearla. null en insignias que no son de racha. */
  semanasRequeridas?: number | null;
  /** Icono PNG del catálogo. */
  iconUrl?: string | null;
  color?: string | null;
  isActive?: boolean;
  /**
   * Fecha de desbloqueo. Extensión del mock: el backend no la emite hoy (BadgeDto no la tiene), así
   * que contra datos reales siempre es undefined y el año no se pinta.
   */
  unlockedAt?: string | null;
}

/**
 * Estado de la racha del usuario (GET v1/streaks/me). SIN puntos (regla de oro).
 * Espejo de PMP.SharedContracts.DTOs.Recognition.StreakDto.
 */
export interface StreakDto {
  userId: string;
  /** Rama B — programa de esta racha ("Fundamentales" | "Brillamos" | "Personalizada"). Ausente = "Fundamentales". */
  programa?: string | null;
  /** Semanas consecutivas activas al corte actual. */
  semanasActuales: number;
  /** Récord de la campaña vigente (decisión de producto 2026-08-23): se reinicia al cambiar de
   * campaña, igual que `semanasActuales`. Con la Rama B apagada, es el histórico de siempre. */
  record: number;
  /** Reconocimientos RECIBIDOS (RN-G08, no enviados) con origen Brillamos Juntos, en vivo. Acotado a
   * la campaña vigente (decisión de producto 2026-08-23), no a todo el histórico de campañas Brillamos. */
  totalNominaciones: number;
  /** Actividad de los últimos 7 días (índice 0 = lunes). */
  actividadSemanal: boolean[];
  /** Insignias desbloqueadas. */
  insignias: BadgeDto[];
  /** Racha congelada por calendario (freeze). */
  congelada: boolean;
  /** Racha en riesgo de romperse esta semana (nudge). */
  enRiesgo: boolean;
  /**
   * Catálogo completo de insignias de racha activas, ordenado por umbral (RF-038). Es lo que el
   * widget pinta —ganadas y pendientes— para no hardcodear umbrales ni nombres en el cliente.
   * Opcional: un backend anterior al cambio no lo envía.
   */
  catalogoInsignias?: BadgeDto[] | null;
  /**
   * RF-037 — Período sobre el que se cuenta la racha. Con 'monthly', `semanasActuales` y `record`
   * cuentan MESES consecutivos y los umbrales de las insignias se alcanzan en meses: el rótulo
   * "semanas" de esta pantalla deja de ser cierto. Alinear ese vocabulario está pendiente (RF-038 y
   * RF-041/042); este campo es lo que lo hace posible. Opcional: un backend anterior no lo envía.
   */
  periodicidad?: StreakPeriodicity | null;
  /**
   * RF-043 — `false` cuando el Admin Global apagó la racha desde Gestión. Ninguna vista puede
   * pintar racha, tracker ni insignias de racha con este valor en `false`.
   *
   * Opcional a propósito, y por eso el gate se escribe SIEMPRE como `habilitada === false` y nunca
   * como `!habilitada`: un backend anterior al cambio no emite el campo, y con la forma negada la
   * ausencia apagaría la racha en todos los despliegues durante un despliegue escalonado. Se oculta
   * ante el `false` explícito, no ante el desconocimiento.
   */
  habilitada?: boolean | null;
  /**
   * RF-036 — Envíos CALIFICADOS del período EN CURSO (el que aún no ha cerrado), con la misma regla
   * anti-abuso que aplicará el cierre. Con `minimoPeriodo` permite pintar el progreso real
   * ("2 de 3 esta semana") en vez de un aviso que solo aparece cuando ya va tarde.
   *
   * Opcional: un backend anterior al cambio no lo envía, y entonces no se pinta el progreso — nunca
   * se sustituye por 0, que en pantalla significa "no has enviado nada" y sería falso.
   */
  enviosPeriodo?: number | null;
  /**
   * RF-036/RF-037 — Mínimo EFECTIVO del período para esta persona (el de su campaña si define uno,
   * si no el global de Gestión). Lo resuelve el backend con el mismo servicio que usa el cierre:
   * NO se recalcula aquí. Opcional, igual que `enviosPeriodo`.
   */
  minimoPeriodo?: number | null;
  /**
   * RF-036 — `true` cuando el período EN CURSO ya califica y por tanto YA está sumado en
   * `semanasActuales`/`record`, antes de que el cierre lo consolide.
   *
   * No se deduce de `enviosPeriodo >= minimoPeriodo`: el freeze también hace contar el período, y
   * duplicar la condición aquí es como se desincronizan las dos superficies. Opcional (aditivo).
   */
  periodoEnCursoCuenta?: boolean | null;
  /**
   * RF-036/RF-038 — Códigos de insignias que el récord PROYECTADO alcanza pero que el cierre aún no
   * ha otorgado. Sirven para que la racha proyectada no conviva con su propia insignia bajo candado.
   *
   * NO son ganadas: viajan aparte de `insignias` a propósito, no hay `BadgeEarnedEvent` detrás y
   * desaparecen si el período deja de calificar. Se pintan distinguidas, nunca como definitivas, y el
   * umbral lo decide el backend (recalcularlo aquí es lo que RF-038 prohíbe). Opcional (aditivo).
   */
  insigniasProvisionales?: string[] | null;
  /**
   * Insignias de GANADORES (categoría Campaign) ya confirmadas — la medalla que otorga un
   * administrador desde "Ganadores" (REQ-085), no una insignia de racha.
   *
   * Independiente de `habilitada`: una medalla de Ganadores ya otorgada no debe desaparecer del
   * Perfil porque alguien apague la funcionalidad de racha (RF-043 solo gobierna la racha). Por eso
   * `useMyCampaignBadges` lee este campo directo de `useMyStreak`, sin pasar por `useVisibleStreak`.
   * Opcional: un backend anterior al cambio no lo envía.
   */
  insigniasCampana?: BadgeDto[] | null;
}

// ─── Búsqueda de personas (UserAdminService — selector de receptores / a nombre de) ───

/**
 * Persona resumida para el selector de receptores. Subconjunto de UserDto que el frontend
 * necesita (sin puntos, sin datos sensibles de más). Espejo parcial de PMP.SharedContracts.DTOs.Users.UserDto.
 */
export interface PersonSummary {
  id: string;
  email: string;
  displayName: string | null;
  jobTitle?: string | null;
  ouId?: string | null;
  ouName?: string | null;
  /** Foto de AD/Entra ID (ver `UserItem.photoUrl` — PENDIENTE de backend). */
  photoUrl?: string | null;
  /**
   * RF-008 — avatar que esa persona eligió al darse de alta (`UserDto.Avatar`: el **id**, o sea el
   * nombre del blob, nunca la URL). `null` para quien no eligió ninguno.
   *
   * Existe porque `photoUrl` sigue llegando siempre vacío, así que sin esto las pantallas que pintan a
   * OTRAS personas se quedaban en las iniciales aunque esa persona sí hubiera elegido su avatar.
   * `PersonAvatar` lo resuelve a URL y aplica la precedencia (Graph manda).
   */
  avatar?: string | null;
}

// ─── Mi Equipo — REQ-086 ──────────────────────────────────────────────────

/** Espejo de PMP.SharedContracts.DTOs.Users.ManagerStatusDto. */
export interface ManagerStatusDto {
  userId: string;
  isManager: boolean;
  managerId?: string | null;
  ancestorIds: string[];
}

/** Respuesta paginada del UserAdminService (GET v1/users). */
export interface UserSearchResponse {
  items: PersonSummary[];
  totalCount: number;
  page: number;
  pageSize: number;
}

/** Parámetros de búsqueda de personas. */
export interface PeopleSearchParams {
  search: string;
  ouId?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Cuerpo de error estándar (RFC 7807 Problem Details) que devuelven todos los microservicios.
 * Espejo de PMP.SharedContracts.DTOs.Common.ErrorDto. `title` es el mensaje para el usuario;
 * `errors` trae el detalle por campo en validaciones 400.
 */
export interface RecognitionErrorBody {
  type: string;
  title: string;
  status: number;
  correlationId: string;
  errors?: Record<string, string[]>;
}

// ─── WP-11 REQ-106 — Carga masiva CSV (RecognitionService) ──────────────────────
// RN-CSV01..04 / REQ-085. Contrato CONGELADO (28-jul-2026) — espejo exacto de
// PMP.SharedContracts.DTOs.Recognition.RecognitionBulkImport*Dto. No adaptar desde aquí.

/** Fila de entrada al enviar el lote. Espejo de RecognitionBulkImportRowDto. */
export interface RecognitionBulkImportRowDto {
  emisorEmail?: string | null;
  receptorEmail?: string | null;
  fundamentalIds: string[];
  mensaje?: string | null;
  campanaId?: string | null;
  externalId?: string | null;
  emisorNomina?: string | null;
  receptorNomina?: string | null;
  behaviorIds?: string[] | null;
  habitIds?: string[] | null;
  lineNumber?: number;
}

/**
 * Código estable de error de fila. La lista es la documentada en el contrato; el backend puede
 * en teoría enviar otro string, por eso el tipo público de `code` en
 * {@link RecognitionBulkImportRowErrorDto} es `string` — este union solo documenta los valores
 * conocidos para el mapeo de mensajes (ver `views/recognition/bulkUpload/errorMessages.ts`).
 */
export type RecognitionBulkImportErrorCode =
  | 'receptor-required'
  | 'receptor-not-found'
  | 'fundamental-invalid'
  | 'self-recognition'
  | 'blocked-word'
  | 'weekly-cap-exceeded'
  | 'duplicate-in-file'
  | 'campaign-closed'
  | 'not-allowed-for-campaign'
  | 'behavior-invalid'
  | 'habit-invalid'
  | 'multiple-people-not-supported';

/** Error de una fila concreta. Espejo de RecognitionBulkImportRowErrorDto. */
export interface RecognitionBulkImportRowErrorDto {
  lineNumber: number;
  receptorEmail: string | null;
  code: string;
  message: string;
}

/** Resultado de la importación masiva. Espejo de RecognitionBulkImportResultDto. */
export interface RecognitionBulkImportResultDto {
  received: number;
  created: number;
  skipped: number;
  preview: boolean;
  rowErrors: RecognitionBulkImportRowErrorDto[];
  errorsCsv: string;
  receptoresNoEncontrados: string[];
  /**
   * Subconjunto de `skipped`: filas omitidas por idempotencia (ya existía el mismo reconocimiento
   * — mismo emisor/receptor/Fundamental/mensaje — en esta campaña). No es un error: el mismo mensaje
   * a otra persona o en otra campaña sí se crea. Aditivo: `0`/ausente si el backend es anterior a
   * este campo.
   */
  duplicadosOmitidos?: number;
}

// ─── REQ-071 — Bandeja de notificaciones in-app (NotificationService) ──

/**
 * Tipo/escenario espejo de una notificación in-app (RecognitionTemplateType.ToKey). Es texto libre del
 * backend; se modela como unión abierta (string) para no romper cuando aparezcan tipos nuevos. Los
 * valores conocidos determinan el icono contextual del banner.
 */
export type NotificationType =
  | 'recognitionReceived'
  | 'streakAndBadges'
  | 'badgeEarned'
  | 'campaignUpdate'
  | 'campaignApproval'
  | 'inviteToRecognize'
  | 'digest'
  | (string & {});

/**
 * Notificación de la bandeja in-app del empleado (GET v1/notifications/inbox).
 * Espejo EXACTO de PMP.SharedContracts.DTOs.Recognition.InboxNotificationDto (camelCase). SIN puntos.
 */
export interface InboxNotificationDto {
  id: string;
  userId: string;
  /** Tipo espejo (ej: "recognitionReceived", "campaignUpdate"). */
  type: NotificationType;
  title: string;
  body: string;
  /** Deep-link opcional al que navegar al abrir (ej: /app/reconocer, /app/brillamos) — REQ-073. */
  link: string | null;
  isRead: boolean;
  /** Fecha de creación (ISO 8601 / DateTimeOffset). */
  createdAt: string;
  /** Nombre del actor que originó la notificación (ej: quien reconoció), si aplica. */
  actorName?: string | null;
  /**
   * RF-165 — Etiqueta YA LOCALIZADA de la acción que encadena el programa ("Invita a reconocer").
   * Llega traducida del servicio, que es quien conoce el idioma del destinatario; la pantalla no la
   * traduce. Null si el aviso no propone ninguna acción.
   */
  ctaLabel?: string | null;
  /**
   * RF-165 — Deep-link de esa acción, distinto de `link`: aquel lleva al contenido del aviso y este a
   * la acción siguiente.
   */
  ctaLink?: string | null;
}

/**
 * Respuesta paginada de la bandeja: items + contador de no leídas para el badge.
 * Espejo de InboxPageDto (GetInboxQuery). `unreadCount`/`total` llegan como number (long serializado).
 */
export interface NotificationInboxResponse {
  items: InboxNotificationDto[];
  unreadCount: number;
  total: number;
  page: number;
  pageSize: number;
}

// ─── REQ-070 — Preferencias self-service de notificaciones (NotificationService) ──

/**
 * Claves canónicas de categoría que ACEPTA el backend (RecognitionNotificationCategory.ToKey).
 * Cualquier clave fuera de este set es descartada por el servidor al guardar.
 */
export type NotificationCategoryKey =
  | 'recognitionReceived'
  | 'streakAndBadges'
  | 'campaigns'
  | 'digest';

/** Orden de presentación de los toggles de categoría en el perfil. */
export const NOTIFICATION_CATEGORY_KEYS: NotificationCategoryKey[] = [
  'recognitionReceived', 'streakAndBadges', 'campaigns', 'digest',
];

/**
 * Preferencias self-service del usuario (GET/PUT v1/notifications/recognition/me/preferences).
 * Espejo de PMP.SharedContracts.DTOs.Recognition.NotificationSelfPreferenceDto. `categorias`: clave →
 * habilitado; la ausencia de una clave = hereda el valor resuelto por la config de admin.
 */
export interface NotificationSelfPreferenceDto {
  userId: string;
  /** Interruptor global de correo (false = desuscrito de todo email). */
  emailEnabled: boolean;
  /** Toggles por categoría (solo se persisten las claves de NOTIFICATION_CATEGORY_KEYS). */
  categorias: Partial<Record<NotificationCategoryKey, boolean>>;
}

// ─── RF-162 — Configuración jerárquica de notificaciones (admin) ────────────────

/**
 * Nivel jerárquico de una configuración. Espejo de `PMP.NotificationService.Domain.Enumerations
 * .PreferenceScope`. Viajan como NÚMERO: el backend no registra `JsonStringEnumConverter`, así que
 * System.Text.Json serializa el enum por su valor.
 *
 * La precedencia de resolución es Site > BU > OU > Global; el orden numérico de aquí NO la expresa
 * (Global es 0 y es el de MENOR precedencia) — usar `PREFERENCE_SCOPE_ORDER` para presentar.
 */
export const PREFERENCE_SCOPE = { Global: 0, Ou: 1, Bu: 2, Site: 3 } as const;
export type PreferenceScope = (typeof PREFERENCE_SCOPE)[keyof typeof PREFERENCE_SCOPE];

/** Niveles de MENOR a MAYOR precedencia, para pintar el selector en el orden en que se resuelven. */
export const PREFERENCE_SCOPE_ORDER: PreferenceScope[] = [
  PREFERENCE_SCOPE.Global, PREFERENCE_SCOPE.Ou, PREFERENCE_SCOPE.Bu, PREFERENCE_SCOPE.Site,
];

/** Clave i18n de cada nivel (`admin.notifications.scope.*`). */
export const PREFERENCE_SCOPE_KEY: Record<PreferenceScope, string> = {
  [PREFERENCE_SCOPE.Global]: 'global',
  [PREFERENCE_SCOPE.Ou]: 'ou',
  [PREFERENCE_SCOPE.Bu]: 'bu',
  [PREFERENCE_SCOPE.Site]: 'site',
};

/** Frecuencia de correo no transaccional. Espejo de `MailFrequency` (también numérico). */
export const MAIL_FREQUENCY = { Daily: 0, Weekly: 1, Biweekly: 2, None: 3 } as const;
export type MailFrequency = (typeof MAIL_FREQUENCY)[keyof typeof MAIL_FREQUENCY];

/**
 * Documento CRUDO de un nivel (GET v1/notifications/recognition/settings?scope=&scopeId=).
 * Espejo de `RecognitionPreferenceDto`.
 *
 * `null` en un toggle significa **heredado del nivel inferior**, NO "apagado": por eso los campos son
 * `boolean | null` y no `boolean`. Es la diferencia con `ResolvedPreferences`, donde cada valor viene
 * ya mezclado y nunca es null.
 */
export interface RecognitionPreferenceDto {
  scope: PreferenceScope;
  scopeId: string | null;
  globalEnabled: boolean | null;
  /**
   * WP-F4-b — la campanita y el Activity Feed de Teams. `null` = heredado.
   *
   * 🔴 NO cuelgan del maestro `globalEnabled`, que gobierna el CORREO: la maqueta dibuja tres filas,
   * una por canal, y colgarlos apagaría los tres de un clic. Hasta WP-F4-b estos dos canales se
   * enviaban SIEMPRE, sin consultar nada.
   */
  inAppEnabled: boolean | null;
  teamsEnabled: boolean | null;
  recognitionReceivedEnabled: boolean | null;
  streakAndBadgesEnabled: boolean | null;
  campaignsEnabled: boolean | null;
  digestEnabled: boolean | null;
  monthlySummaryEnabled: boolean | null;
  frequency: MailFrequency | null;
  ouFrequencyExceptions: Record<string, MailFrequency>;
  blockedEmails: string[];
  /** false = este nivel aún no tiene documento propio; hereda cada valor del nivel inferior. */
  exists: boolean;
}

/** Cuerpo del PUT v1/notifications/recognition/settings (espejo de SaveRecognitionPreferenceRequest). */
export interface SaveRecognitionPreferenceRequest {
  scope: PreferenceScope;
  scopeId?: string | null;
  globalEnabled?: boolean | null;
  /** WP-F4-b — la campanita. `null` = heredado. */
  inAppEnabled?: boolean | null;
  /** WP-F4-b — el Activity Feed de Teams. `null` = heredado. */
  teamsEnabled?: boolean | null;
  recognitionReceivedEnabled?: boolean | null;
  streakAndBadgesEnabled?: boolean | null;
  campaignsEnabled?: boolean | null;
  digestEnabled?: boolean | null;
  monthlySummaryEnabled?: boolean | null;
  frequency?: MailFrequency | null;
  ouFrequencyExceptions?: Record<string, MailFrequency> | null;
  blockedEmails?: string[] | null;
  /**
   * WP-F4-b · 6.2 — Interruptores por (tipo × canal), clave `plantilla:canal`.
   *
   * 🔴 La AUSENCIA de una clave significa «sigue a tu categoría», así que se manda el diccionario
   * COMPLETO: el documento se reemplaza entero y mandar solo los cambios borraría los interruptores
   * que ya estaban puestos en ese nivel.
   */
  typeChannelOverrides?: Record<string, boolean> | null;
}

/**
 * Configuración EFECTIVA ya resuelta para un contexto (GET .../settings/resolve).
 * Espejo de `ResolvedPreferences` — aquí ningún toggle es null.
 */
export interface ResolvedPreferencesDto {
  globalEnabled: boolean;
  inAppEnabled: boolean;
  teamsEnabled: boolean;
  recognitionReceivedEnabled: boolean;
  streakAndBadgesEnabled: boolean;
  campaignsEnabled: boolean;
  digestEnabled: boolean;
  monthlySummaryEnabled: boolean;
  frequency: MailFrequency;
  emailBlocked: boolean;
}

// ─── WP-15 REQ-127 — Roles y Permisos (UserAdminService) ────────────────────────

/**
 * Permisos administrables — 23 claves canónicas. Espejo EXACTO de
 * PMP.SharedContracts.Authorization.PermissionKeys.Order (backend). Cada clave es un permiso
 * booleano por administrador.
 */
export type PermissionKey =
  | 'manageUsers'
  | 'manageRecognitions'
  | 'manageNotifications'
  | 'manageDocuments'
  | 'manageWinners'
  | 'viewAnalytics'
  | 'viewReports'
  | 'exportData'
  | 'configureViews'
  | 'bulkCsv'
  | 'moderateContent'
  | 'manageCampaigns'
  | 'createCampaigns'
  | 'readOnly'
  | 'approveNominations'
  | 'managePoints'
  | 'manageRoles'
  | 'manageBlockedWords'
  | 'managePointsConfig'
  | 'manageRecap'
  | 'delegateRecognition'
  | 'accessCatalogFundamentals'
  | 'accessCatalogOus';

/** Orden canónico de los 23 permisos. Espejo de PMP.SharedContracts.Authorization.PermissionKeys.Order. */
/**
 * Excepción por localidad que aplica AL USUARIO ACTUAL — respuesta de
 * `GET v1/campaign-exceptions/active-for-me`. Sólo identidad: el backend NO sirve aquí los metadatos
 * de administración de `CampaignExceptionDto` (`solicitadoPor`/`resueltoPor`/`comentario`/`alcance`),
 * porque este endpoint lo consume cualquier colaborador. Con el `id` se piden sus documentos.
 */
export interface ActiveCampaignExceptionDto {
  id: string;
  tipo: CampaignExceptionType;
}

export const PERMISSION_ORDER: PermissionKey[] = [
  'manageUsers', 'manageRecognitions', 'manageNotifications', 'manageDocuments', 'manageWinners',
  'viewAnalytics', 'viewReports', 'exportData', 'configureViews', 'bulkCsv', 'moderateContent',
  'manageCampaigns', 'createCampaigns', 'readOnly', 'approveNominations', 'managePoints',
  'manageRoles', 'manageBlockedWords', 'managePointsConfig', 'manageRecap', 'delegateRecognition',
  'accessCatalogFundamentals', 'accessCatalogOus',
];

/**
 * RN-R03 — permisos de LECTURA. Espejo literal de `PermissionKeys.OperationOf`, que los enumera
 * uno a uno (`ViewAnalytics`, `ViewReports`, `ExportData`) y manda todo lo demás al caso `_`.
 *
 * 🔴 Se escriben los LITERALES a propósito, no se derivan de una convención de nombre. Una regla
 * del estilo «lo que empieza por view/export es lectura» parece más limpia y **fallaría el día que
 * alguien añada un permiso de escritura llamado `exportSchedule`** — y fallaría en silencio,
 * abriendo el conflicto que esto existe para cerrar. Es la misma lección de `COMODIN_IDIOMA`: cuando
 * un valor es un contrato con otra pieza, se afirma literal.
 */
const READ_PERMISSIONS: readonly PermissionKey[] = ['viewAnalytics', 'viewReports', 'exportData'];

/**
 * RN-CSV04 — `bulkCsv` MUTA, pero queda excluido de la regla RN-R03 a propósito: se habilita por
 * rol/persona y campaña de forma independiente al perfil de consulta (el caso del antiguo
 * Supervisor / Admin Local País delegado). Espejo de la excepción que
 * `PermissionKeys.CountsAsWriteForReadOnlyRule` documenta en su propio cuerpo.
 */
const READONLY_RULE_EXEMPT: readonly PermissionKey[] = ['bulkCsv'];

/**
 * ¿Cuenta este permiso como ESCRITURA para el conflicto de RN-R03? Espejo de
 * `PMP.SharedContracts.Authorization.PermissionKeys.CountsAsWriteForReadOnlyRule`.
 *
 * 🔴 El servidor rechaza el conjunto en conflicto con 422 (`AppUser.ReplaceRecognitionPermissions`
 * → `RoleAssignmentConflictException`). Esta función NO sustituye a esa guarda: existe para que la
 * pantalla no deje construir un estado que el servidor va a rechazar al Guardar.
 */
export function countsAsWriteForReadOnlyRule(key: PermissionKey): boolean {
  if (key === 'readOnly') return false;               // es el MODIFICADOR, no una escritura
  if (READ_PERMISSIONS.includes(key)) return false;
  if (READONLY_RULE_EXEMPT.includes(key)) return false;
  return true;
}

/** true si el conjunto tiene «Solo lectura» junto a algún permiso de escritura (RN-R03). */
export function hasReadOnlyWriteConflict(permissions: PermissionMap): boolean {
  if (!permissions.readOnly) return false;
  return PERMISSION_ORDER.some((k) => permissions[k] && countsAsWriteForReadOnlyRule(k));
}

/**
 * Aplica RN-R03 al encender/apagar un permiso: devuelve el mapa YA sin conflicto.
 *
 *  · encender `readOnly`  ⇒ se apagan los de escritura (los de lectura y `bulkCsv` se conservan);
 *  · encender una escritura ⇒ se apaga `readOnly`;
 *  · apagar cualquier cosa ⇒ no puede crear conflicto, se deja tal cual.
 *
 * 🔴 Opera sobre `PERMISSION_ORDER` ENTERO, no sobre lo que la pantalla muestra. El modal oculta 4
 * permisos a petición de la clienta (`HIDDEN_PERMISSIONS`), y uno oculto y encendido mantendría el
 * conflicto vivo **e invisible**: el admin vería «Solo lectura» sin nada más marcado y el servidor
 * le devolvería 422 sin que nada en pantalla lo explicara.
 */
export function applyReadOnlyRule(permissions: PermissionMap, changed: PermissionKey): PermissionMap {
  if (!permissions[changed]) return permissions;      // se acaba de APAGAR: nunca crea conflicto

  if (changed === 'readOnly') {
    const next = { ...permissions };
    for (const k of PERMISSION_ORDER) {
      if (countsAsWriteForReadOnlyRule(k)) next[k] = false;
    }
    return next;
  }

  return countsAsWriteForReadOnlyRule(changed) ? { ...permissions, readOnly: false } : permissions;
}

/** Los dos roles reales de Brillamos que se administran desde esta pantalla (más AdminGlobal,
 *  que no se asigna aquí pero puede aparecer en la tabla si ya lo tiene). Espejo de
 *  PMP.SharedContracts.Enumerations.UserRole — NO existen AdminFundamentals/AdminOuBu/
 *  AdminLocalCountry: se consolidaron en estos dos el 2026-07-28. */
export type AdminRoleKey = 'AdminGlobal' | 'AdminBrillamos' | 'AdminOUBrillamos';

/**
 * WP-F3 / REQ-136 — «Vistas de Usuario»: qué vistas del colaborador ofrece una OU.
 * Espejo de `PMP.SharedContracts.DTOs.Recognition.UserViewSettingsDto`.
 *
 * 🔴 Responde UNA pregunta: «¿esta OU ofrece esta vista?». NO responde «¿este usuario puede verla
 * ahora?». Seis de las doce ya tienen un interruptor vivo por otro camino (kill-switch,
 * anti-spoiler, toggle de campaña, Wrapped, `reportEnabled`, ser manager) y **se componen con Y
 * lógico**: la vista se muestra si la OU la ofrece Y su propia regla lo permite. Hacer de esto la
 * única verdad habría dejado seis pares de mecanismos compitiendo por la misma pregunta.
 */
/**
 * WP-F4-b · 6.3 — Una fila de las dos tablas de Notificaciones.
 *
 * 🔴 La lista de filas la declara el BACKEND, no la pantalla: la maqueta dibuja trece y solo NUEVE
 * corresponden a un aviso que el sistema emita. Pintarlas a mano aquí habría vuelto a meter las
 * cuatro decorativas —y una fila nueva del catálogo no aparecería nunca—.
 */
export interface NotificationRowDto {
  /** `shine` o `admin`: cuál de las dos tablas la contiene. */
  table: 'shine' | 'admin';
  /** Clave kebab de la plantilla. Identifica la fila y forma la de sus interruptores. */
  key: string;
  /** El asunto por idioma. Vienen los siete, aunque la tabla solo enseñe el español. */
  subjects: Record<string, string>;
  /**
   * Estado de los tres interruptores EN ESTE NIVEL. `null` = heredado, que no es lo mismo que
   * apagado y se pinta distinto — igual que el resto de la pantalla.
   */
  channels: Record<string, boolean | null>;
}

/**
 * WP-F4-b · 6.4 — «Avisos del sistema». Los cuatro tipos que dibuja la maqueta; solo cambian el
 * icono y el color, no el destinatario.
 */
export const SYSTEM_BROADCAST_KIND = {
  Maintenance: 0,
  Suspension: 1,
  Restored: 2,
  General: 3,
} as const;

export type SystemBroadcastKind =
  (typeof SYSTEM_BROADCAST_KIND)[keyof typeof SYSTEM_BROADCAST_KIND];

export const SYSTEM_BROADCAST_AUDIENCE = { Everyone: 0, SingleOu: 1 } as const;

export type SystemBroadcastAudience =
  (typeof SYSTEM_BROADCAST_AUDIENCE)[keyof typeof SYSTEM_BROADCAST_AUDIENCE];

export interface SystemBroadcastRequest {
  kind: SystemBroadcastKind;
  audience: SystemBroadcastAudience;
  subject: string;
  message: string;
  /** Obligatoria cuando la audiencia es una OU: sin ella el aviso iría a TODA la organización. */
  ouId?: string | null;
}

/**
 * 🔴 `skipped` no es ruido: son personas cuya identidad no se pudo resolver y que NO recibieron el
 * aviso. Un mantenimiento que llega a la mitad de la plantilla y dice «enviado» es peor que uno que
 * falla, porque nadie va a volver a mirarlo.
 */
export interface SystemBroadcastResult {
  recipients: number;
  sent: number;
  skipped: number;
}

/** Los tres canales de cada fila, en el orden en que la maqueta los dibuja. */
export const NOTIFICATION_ROW_CHANNELS = ['inapp', 'email', 'teams'] as const;

export type NotificationRowChannel = (typeof NOTIFICATION_ROW_CHANNELS)[number];

export interface UserViewSettingsDto {
  /** OU a la que aplica. `null` = documento global. */
  ouId: string | null;
  medallero: boolean;
  perfilUsuario: boolean;
  /** ⚠️ Se compone con el kill-switch `sendingDisabled`. */
  enviarReconocimiento: boolean;
  /** ⚠️ Se compone con el toggle de campaña de `E8` (RN-PR09). */
  historicoPorAnio: boolean;
  /** 🔴 El ÚNICO que nace APAGADO: es el único que la maqueta dibuja sin `checked`. */
  leaderboard: boolean;
  feedActividad: boolean;
  /** ⚠️ Se compone con `wrappedByOu` y con RN-PR10. */
  resumenMensual: boolean;
  /** ⚠️ Se compone con el Modo Anti-Spoiler. */
  busquedaCompaneros: boolean;
  vistaFundamentales: boolean;
  /** ⚠️ Se compone con `ModerationSettings.reportEnabled`. */
  banderaReportar: boolean;
  recordatorioDiario: boolean;
  /** ⚠️ Se compone con ser manager y con el permiso de métricas. */
  verATuEquipo: boolean;
  /** CPE-160 — URL del micrositio por **Id** del Fundamental (no por posición: el catálogo es dinámico). */
  micrositeUrls?: Record<string, string> | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

/** Las 12 claves de vista, en el orden de la maqueta. Sirve para pintar y para recorrer. */
export const USER_VIEW_KEYS = [
  'medallero', 'perfilUsuario', 'enviarReconocimiento', 'historicoPorAnio', 'leaderboard',
  'feedActividad', 'resumenMensual', 'busquedaCompaneros', 'vistaFundamentales',
  'banderaReportar', 'recordatorioDiario', 'verATuEquipo',
] as const;

export type UserViewKey = (typeof USER_VIEW_KEYS)[number];

/**
 * Defaults de la maqueta, para cuando la consulta aún no ha respondido.
 *
 * 🔴 Se falla ABIERTO (todo `true` salvo `leaderboard`) y no cerrado, al revés que la mayoría de
 * gates de este módulo. El motivo es que aquí lo que se protege no es un dato: es la interfaz.
 * Fallar cerrado dejaría la aplicación medio vacía durante cada carga y en cada error de red — y
 * quien esté detrás de un gate de verdad (kill-switch, anti-spoiler, permiso) sigue estándolo,
 * porque estas vistas se componen con esas reglas, no las sustituyen.
 */
export const USER_VIEWS_DEFAULT: Record<UserViewKey, boolean> = {
  medallero: true, perfilUsuario: true, enviarReconocimiento: true, historicoPorAnio: true,
  leaderboard: false, feedActividad: true, resumenMensual: true, busquedaCompaneros: true,
  vistaFundamentales: true, banderaReportar: true, recordatorioDiario: true, verATuEquipo: true,
};

/** Mapa de permiso → habilitado. */
export type PermissionMap = Record<PermissionKey, boolean>;

/**
 * Administrador del módulo con su rol, alcance por OU y matriz de permisos.
 * Espejo de PMP.SharedContracts.DTOs.Users.RecognitionAdminDto.
 */
export interface AdminUserDto {
  id: string;
  email: string;
  displayName: string | null;
  role: AdminRoleKey;
  /** Ids de OU (cualquier nivel del árbol) que administra. Vacío = alcance Global (AdminGlobal/AdminBrillamos). */
  scopeOuIds: string[];
  /** Nombres resueltos de scopeOuIds, en el mismo orden — solo para mostrar. */
  scopeOuNames: string[];
  active: boolean;
  permissions: PermissionMap;
}

/** Cuerpo de PUT v1/users/{id}/recognition-role.
 *
 * `role` admite `'Employee'` además de `AdminRoleKey`: es lo que envía la acción "Quitar"
 * de AdminRoles.tsx para degradar a un admin de Brillamos a colaborador estándar — el propio
 * backend lo permite (`RoleGrantPolicy._brillamosRoles` incluye `UserRole.Employee`), solo que
 * el selector de rol del modal de alta/edición nunca lo ofrece como opción elegible. */
export interface UpdateUserRolesBody {
  role: AdminRoleKey | 'Employee';
  scopeOuIds: string[];
  active: boolean;
  permissions: PermissionMap;
}

// ─── WP-15 REQ-128 — Configuración de Puntos (RulesEngine) ───────────────────────

/** Segmento de población para la config de puntos (espejo staff/frontline). */
export type PointsSegment = 'staff' | 'frontline';

/**
 * Un criterio de scoring y su valor de puntos. Espejo de
 * PMP.SharedContracts.DTOs.Recognition.PointsCriterionDto — SIN `id` en el wire: el frontend
 * deriva la clave de fila de `categoria + criterio` (ver AdminPoints.tsx).
 */
export interface PointCriterionDto {
  /** ETIQUETA editable de la maqueta ("Source of Nomination"). Es lo que se PINTA. */
  categoria: string;
  /** ETIQUETA editable ("Mismo equipo (mismo line manager…)"). Es lo que se PINTA. */
  criterio: string;
  /** Puntos configurables 0-9. NUNCA visible al empleado; esta pantalla es admin-gated. */
  puntos: number;
  activo: boolean;

  /**
   * 🔴 Las CLAVES canónicas del motor (`origen` / `mismo_equipo`). Viajaban en el JSON desde la Ola 0
   * pero faltaban en este tipo, y esa omisión hizo que la columna "Total acumulado" casara por la
   * ETIQUETA: nunca encontraba nada y pintaba 0 en las nueve filas.
   *
   * Es literalmente el defecto que dejó los puntos en cero durante meses —lookup por la etiqueta en
   * vez de por la clave—, esta vez en el front. Todo lo que compare criterios con el motor usa
   * ESTAS, nunca `categoria`/`criterio`.
   *
   * Nulas en configuraciones anteriores a la migración label→key.
   */
  categoriaKey?: string | null;
  criterioKey?: string | null;
}

/**
 * Configuración de puntos de una OU para un segmento (GET/PUT v1/rules/points-config/{ouId}?segment=).
 * `campaignStarted` bloquea la edición (RN: "Config editable solo sin campaña iniciada").
 * Espejo de PMP.SharedContracts.DTOs.Recognition.PointsConfigDto — GetPointsConfig nunca devuelve
 * 404: si no hay documento todavía, el backend arma un borrador con los 9 criterios por defecto.
 */
export interface PointsConfigDto {
  ouId: string;
  segment: PointsSegment;
  /** Si el scoring de puntos está activo para esta OU + segmento. */
  pointsEnabled: boolean;
  criterios: PointCriterionDto[];
  /** Si la campaña ya inició: la edición se deshabilita. */
  campaignStarted: boolean;

  /**
   * WP-A5 · RN-P09 — si los puntos de una nominación pueden mostrarse a un colaborador de esta OU,
   * **ya RESUELTO por el backend**: el interruptor de la OU Y el suelo duro de GDPR.
   *
   * 🔴 Es de SOLO LECTURA a efectos prácticos. El interruptor no se escribe por aquí sino con un
   * override de la regla (`PATCH v1/rules/{code}/ou-overrides/{ouId}`), porque es una decisión **por
   * OU** y esta config es **por OU y segmento**: mandarla aquí la duplicaría en staff y frontline.
   *
   * Sirve para AVISAR: si el admin lo enciende y esto sigue llegando `false`, la OU está sujeta a
   * GDPR y no hay interruptor que valga. Decirlo es la diferencia entre un control que parece roto
   * y uno que se entiende.
   *
   * Opcional porque los emisores anteriores al campo no lo mandan.
   */
  visibleToEmployees?: boolean;
}

/** Una fila del conteo de nominaciones por criterio (WP-A4). */
export interface CriterioNominationCount {
  /** Clave canónica del motor: `origen` | `frecuencia` | `engagement` | `breadth` | `excepcion`. */
  categoria: string;
  /** Clave canónica dentro de la categoría: `mismo_equipo`, `primera_vez`, `tres_mas`… */
  criterio: string;
  /** Nominaciones que activaron este criterio. NUNCA puntos. */
  count: number;
}

/**
 * WP-A4 / RF-039 — resumen de la config de puntos + la columna "Total Acumulado"
 * (GET v1/rules/points-config/summary).
 *
 * 🔴 `nominationCountsByCriterio` trae SIEMPRE las nueve filas del catálogo, con 0 donde no hubo
 * nominaciones: es la maqueta, y una fila ausente no se distingue de una fila en cero.
 */
export interface PointsConfigSummaryDto {
  ouId: string | null;
  segment: PointsSegment | null;
  /** Alcance aplicado tras el cascadeo: "global", "sin-alcance" o el OuId efectivo. */
  scope: string;
  configCount: number;
  /** Suma de PUNTOS CONFIGURADOS. Es un valor de la config, no de nadie. */
  totalAcumulado: number;
  pointsEnabled: boolean;
  nominationCountsByCriterio: CriterioNominationCount[];
  /** Total de reconocimientos del alcance (incluye el diario y lo que no entra en el scoring). */
  totalNominations: number;
  /**
   * 🔴 El pie de la columna: nominaciones DISTINTAS que activaron algún criterio.
   *
   * NO es la suma de `nominationCountsByCriterio`, y por eso viene del servidor en vez de
   * calcularse aquí: los criterios se solapan —una nominación activa origen, frecuencia y breadth a
   * la vez— así que sumar las filas cuenta la misma nominación tres veces.
   */
  nominacionesEvaluadas: number;
}

/** Un override de una BusinessRule para una OU concreta. Gana sobre el valor base. */
export interface RuleOuOverrideDto {
  ouId: string;
  value: string;
}

/**
 * Una BusinessRule en crudo (GET v1/rules/{code}) — solo lo que esta pantalla necesita.
 *
 * `value` es el valor BASE. `IsEnabledForOu` del backend es
 * `isActive && bool.parse(override(ou) ?? value)`, así que los dos campos mandan y los overrides
 * ganan sobre el base.
 */
export interface BusinessRuleDto {
  code: string;
  value: string;
  isActive: boolean;
  ouOverrides?: RuleOuOverrideDto[] | null;
}

/**
 * REQ-038 — estado resuelto del interruptor de puntos para una OU/segmento
 * (GET v1/rules/points-config/toggle). Espejo exacto de `PointsToggleDto`.
 *
 * `effectiveEnabled` es la decisión LOCAL (`globalEnabled && ouSegmentEnabled`); RecognitionService
 * la combina además con el toggle de la campaña concreta antes de aplicar puntos, así que un
 * `true` aquí no garantiza que una campaña dada puntúe.
 */
export interface PointsToggleDto {
  ouId: string;
  segment: PointsSegment;
  /** El kill-switch GLOBAL. Default OFF; Europa ON por override de arranque. */
  globalEnabled: boolean;
  /** `PointsConfig.PointsEnabled` de esta OU/segmento. */
  ouSegmentEnabled: boolean;
  /** Default del interruptor de campaña. El estado real por campaña vive en RecognitionService. */
  campaignDefaultEnabled: boolean;
  effectiveEnabled: boolean;
}

/**
 * WP-A6 / RF-066 — una fila del reporte de puntos por persona
 * (GET v1/rules/points-export). Solo lectura: la pantalla no la edita, solo la descarga.
 */
export interface PointsExportRowDto {
  personaId: string;
  ouId: string | null;
  puntos: number;
}

/**
 * WP-A6 / RF-066 — página del reporte de puntos por persona (GET v1/rules/points-export).
 * Paginado en SERVIDOR (tope 1000 filas/página): el consumidor debe recorrer páginas hasta
 * juntar `total` filas antes de exportar, mismo patrón que el export paginado de
 * `AdminWinners.tsx` (`winnersApi.search` + bucle acumulador).
 */
export interface PointsExportPageDto {
  /** Alcance resuelto tras el cascadeo: "global", "sin-alcance" o el OuId efectivo. */
  scope: string;
  filas: PointsExportRowDto[];
  /** Total de filas del alcance, para paginar. */
  total: number;
  page: number;
  pageSize: number;
}

// ─── WP-16 REQ-139 — Registro standalone Frontline (UserAdminService) ────────────

/** Ítem de catálogo (BU/site/departamento) obtenido de la API (NO hardcodeado). */
export interface OrgCatalogItem {
  id: string;
  name: string;
  /** OU a la que pertenece (para filtrar por partición). */
  ouId?: string | null;
}

/** Catálogos que alimentan el formulario de registro (dependientes de la OU). */
export interface FrontlineCatalogs {
  businessUnits: OrgCatalogItem[];
  sites: OrgCatalogItem[];
  departments: OrgCatalogItem[];
}

/** OU seleccionable en el registro (catálogo público). isEurope decide el campo de identidad. */
export interface RegistrationOu {
  id: string;
  name: string;
  isEurope: boolean;
}

/**
 * Aceptación legal que exige el REGISTRO frontline (bloqueante, REQ-007). Espejo de
 * PMP.SharedContracts.DTOs.Recognition.LegalAcceptanceDto (usa `documentKey`, distinto del
 * `LegalAcceptanceBody` del LegalGate post-acceso). El backend asigna el UserId del alta.
 */
export interface FrontlineLegalAcceptance {
  userId?: string;
  documentKey: string;
  version: string;
  acceptedAt: string;
  source?: string;
  /**
   * Evidencia de lo que se MOSTRÓ, cuando no es lo mismo que `version`: `pdf-{guid}` si la persona
   * leyó el PDF del módulo Documentos. `version` lleva la del texto publicado, que es la que las
   * compuertas comparan — separarlas es lo que hace que la firma del alta cuente sin que un
   * reemplazo de fichero invalide nada.
   */
  displayedVersion?: string | null;
}

/**
 * Cuerpo de POST v1/users/frontline-register — ALINEADO al DTO real del backend
 * (PMP.SharedContracts.DTOs.Recognition.FrontlineRegisterDto). Condicional por OU:
 * Europa envía `fechaIngreso`; el resto envía `numNomina`. Password y aceptaciones son OBLIGATORIOS.
 */
export interface FrontlineRegisterBody {
  nombre: string;
  correo: string;
  ouId: string;
  /** Opcionales en el backend. */
  buId?: string | null;
  siteId?: string | null;
  depto?: string | null;
  /** Solo OUs no-Europa (número de nómina / Employee ID). */
  numNomina?: string | null;
  /** Solo Europa (ISO date, fecha de ingreso). Ya no se pide en el alta (se conserva por compatibilidad de contrato). */
  fechaIngreso?: string | null;
  /**
   * Solo Europa — Local ID del padrón (Excel Europa/EE. UU., hasta 8 dígitos), dato de verificación
   * equivalente al número de nómina. Se compara COMPLETO (no un sufijo) contra el padrón. En Europa
   * no hay número de nómina ni fecha de ingreso visible. El backend lo acepta como `LocalId`.
   */
  localId?: string | null;
  password: string;
  aceptaciones: FrontlineLegalAcceptance[];
  preferredLanguage: string;
  /**
   * RF-008 — avatar elegido durante el alta: el **id del catálogo** (`FrontlineAvatar.id`, el nombre
   * del blob, p.ej. `CP-3.png`), nunca la URL. Viaja en el cuerpo del alta y no por `PATCH` porque
   * `/app/registro` es anónima: sin sesión ni id real, el PATCH daría 401/403.
   *
   * Opcional porque el catálogo puede venir vacío o fallar: en ese caso el alta se completa sin
   * avatar y la app cae al placeholder de iniciales. Si se manda un id que no está en el contenedor,
   * el backend responde **400** con `errors.reason = 'avatar-no-disponible'`.
   */
  avatar?: string | null;
}

/** Respuesta del alta frontline (el backend devuelve UserDto; se usa el id/correo). */
export interface FrontlineRegisterResult {
  id: string;
  email: string;
}

/**
 * Cuerpo de POST v1/users/frontline-activate — ACTIVACIÓN del colaborador cargado por el padrón (carga
 * masiva): desde el enlace del correo (token) FIJA su contraseña y acepta los legales. Deja la cuenta
 * lista para el login. El backend responde UserDto (se usa el id/correo).
 */
export interface FrontlineActivateBody {
  token: string;
  password: string;
  aceptaciones: FrontlineLegalAcceptance[];
}

/** Cuerpo de POST v1/users/frontline-login (login standalone por contraseña, usuarios sin Azure AD). */
export interface FrontlineLoginBody {
  correo: string;
  password: string;
}

/** Respuesta del login frontline: JWT propio emitido por el UserAdminService + datos de sesión. */
export interface FrontlineLoginResult {
  token: string;
  user: {
    id: string;
    email: string;
    displayName?: string | null;
    ouId?: string | null;
    preferredLanguage?: string | null;
  };
}

/** Respuesta de GET v1/users/verify-email?token= — el backend devuelve UserDto (200 = verificado). */
export interface VerifyEmailResult {
  id: string;
  email: string;
}

// ─── RF-008 — Avatar Frontline dinámico (UserAdminService) ───────────────────────

/**
 * Avatar del catálogo DINÁMICO (RF-008, GET v1/users/avatars — anónimo). El backend expone los
 * blobs disponibles: `id` es el nombre del blob (p.ej. "Avatar_3.png") y es lo que se persiste en
 * el perfil del usuario; `url` es una URL con SAS lista para usar directamente en `<img src>`.
 * Pueden ser 1 o N — ya NO es una lista fija de colores dibujados a mano.
 */
export interface FrontlineAvatar {
  id: string;
  url: string;
}

/**
 * Cuerpo de PATCH v1/users/{id}/avatar — persiste el avatar elegido (RF-008). `avatar` es el ID del
 * catálogo (FrontlineAvatar.id — el nombre del blob, p.ej. "Avatar_3.png"), NUNCA una URL.
 */
export interface UpdateAvatarBody {
  /** Id del catálogo dinámico (FrontlineAvatar.id). */
  avatar: string;
}

/**
 * Respuesta de `GET v1/users/{id}/profile` (el `UserProfileDto` del backend). Es **el único endpoint
 * que devuelve `avatar`**: `UserDto` —el que devuelve `resolve`— está congelado y no lo lleva.
 *
 * Sólo se declaran los campos que consume el front. El backend devuelve más; añadirlos aquí sin
 * usarlos sería inventarse un contrato.
 */
export interface UserProfile {
  id: string;
  /** Id del catálogo de avatares (nombre del blob, p.ej. `CP-3.png`), o `null` si no eligió ninguno. */
  avatar?: string | null;
  displayName?: string | null;
  email?: string | null;
}

// ─── WP-16 REQ-140 — Aceptaciones legales (UserAdminService, ARCO) ───────────────

/** Tipo de documento legal versionado. */
export type LegalDocType = 'privacy' | 'legal';

/**
 * Claves con las que el ALTA firmó **hasta el 24-ago-2026**. Ya NO se usan para firmar: quedan porque
 * siguen existiendo en la base y porque el motivo por el que existieron merece contarse entero.
 *
 * **Lo que decía esto el 19-ago, y por qué era correcto entonces.** El alta firmaba
 * `privacy-policy` / `legal-bases` en vez de `privacy` / `legal` a propósito: en el backend
 * `AppUser.AddLegalAcceptance` **reemplaza por `documentKey`**, así que con la clave canónica la
 * aceptación posterior del modal bloqueante **borraba la firma del alta** — la única constancia de
 * que la persona había visto un PDF, porque esa constancia era su `version` (`pdf-…`). Sobrevivía
 * precisamente porque la clave era otra. Y había un segundo motivo: aunque se homologaran, la firma
 * del alta seguiría sin CONTAR, porque las compuertas comparan la versión del texto vigente y un
 * `pdf-…` no empata jamás.
 *
 * **Qué cambió (plan «Gate legal del primer acceso», D3+D4+D6).** La evidencia viaja ahora en su
 * propio campo (`displayedVersion`) y el endpoint de contenido devuelve además la versión comparable
 * (`comparableVersion`). Eso desmonta los dos motivos a la vez: homologar ya no destruye nada, y el
 * alta firma una versión que SÍ empata, así que quien acaba de registrarse deja de ver el modal legal
 * en su primer acceso.
 *
 * **Las firmas viejas no se migran** (D7): inventar a qué versión de texto correspondía cada `pdf-…`
 * sería inventar datos de consentimiento. Conviven como histórico de auditoría, y por eso estas claves
 * siguen nombradas aquí. La prueba que antes exigía usarlas ahora exige lo contrario.
 */
export const LEGAL_LEGACY_SIGNUP_KEYS: Record<LegalDocType, string> = {
  privacy: 'privacy-policy',
  legal: 'legal-bases',
};

// ─── RF-079 — Documentos PDF corporativos (repositorio por OU, §11) ──────────────

/** Tipo de documento corporativo (maqueta §11). Espejo del enum CorporateDocumentType del backend. */
export type CorporateDocType = 'PoliticaPrivacidad' | 'BasesLegales' | 'GuiaUsuario' | 'Premios' | 'CookiesPolicy' | 'LegalNotice';

/** DTO COMPLETO (admin) de un documento corporativo. `url` es una URL SAS de corta vida. */
export interface CorporateDocumentDto {
  id: string;
  nombre: string;
  tipo: CorporateDocType;
  scopeOuIds: string[];
  languages: string[];
  /** URL SAS de lectura (corta vida). Nullable: el backend la declara `string?`. */
  url: string | null;
  fileName: string;
  sizeBytes: number;
  uploadedBy: string;
  uploadedAt: string;
  /** GAP-DOC — banderas de la tabla "Docs Legales" (solo relevantes para docs legales). */
  firstLogin?: boolean;
  triggerUpdate?: boolean;
  /** Archivos por idioma (columnas Versión ES/EN). Clave = código ISO; cada uno con su URL SAS. */
  files?: Record<string, CorporateDocumentFileDto>;
  /** Biblioteca: tipo de medio 'Pdf' | 'Video' | 'Image'. Para Video/Image, `url` es la URL externa. */
  mediaKind?: string;
  externalUrl?: string | null;
}

/** Archivo PDF de un idioma concreto de un documento corporativo (URL SAS + metadata). */
export interface CorporateDocumentFileDto {
  url: string | null;
  fileName: string;
  sizeBytes: number;
}

/** DTO REDUCIDO (for-me / usuario final): sin `uploadedBy` ni `scopeOuIds`. */
export interface CorporateDocumentForMeDto {
  id: string;
  nombre: string;
  tipo: CorporateDocType;
  languages: string[];
  /** URL SAS de lectura (corta vida). Nullable: el backend la declara `string?`. */
  url: string | null;
  fileName: string;
  sizeBytes: number;
  uploadedAt: string;
}

/** Entrada del alta multipart de un documento corporativo. */
export interface UploadCorporateDocumentInput {
  /** Requerido para PDF; ausente para Video/Imagen (que usan externalUrl). */
  file?: File | null;
  nombre: string;
  tipo: CorporateDocType;
  scopeOuIds: string[];
  languages: string[];
  firstLogin?: boolean;
  triggerUpdate?: boolean;
  /** Biblioteca: 'Pdf' (default) | 'Video' | 'Image'. */
  mediaKind?: string;
  /** URL externa para Video/Imagen. */
  externalUrl?: string | null;
}

/** Cuerpo JSON de PUT v1/corporate-documents/{id} — actualiza metadata (sin reemplazar archivo). */
export interface UpdateCorporateDocumentBody {
  nombre: string;
  tipo: CorporateDocType;
  scopeOuIds: string[];
  languages: string[];
  firstLogin?: boolean;
  triggerUpdate?: boolean;
}

/**
 * Documento legal versionado servido por el backend (NO hardcodeado en el front).
 * `version` permite detectar re-aceptación bloqueante cuando cambia. Espejo de LegalDocumentDto.
 */
export interface LegalDocumentDto {
  type: LegalDocType;
  version: string;
  language: string;
  title: string;
  /** Secciones (encabezado + cuerpo) para el visor con scroll-to-accept. */
  sections: { heading: string; body: string }[];
  /**
   * PDF del módulo Documentos que corresponde a este documento legal (LinkDocReg). URL SAS de 15
   * minutos; null/ausente = no hay PDF y se pinta el texto.
   */
  fileUrl?: string | null;
  /** Nombre original del PDF, para el enlace «Abrir en pestaña nueva». */
  fileName?: string | null;
  /**
   * Qué hay que mostrar. 🔴 Lo decide el BACKEND: NO se deduce de `fileUrl != null`, o el día que
   * haya un tercer caso habrá dos verdades. `sections` viene poblado incluso cuando vale `'pdf'`,
   * porque el modal bloqueante de usuarios autenticados sólo entiende secciones.
   */
  contentSource?: 'pdf' | 'text' | 'none';
  /**
   * Versión del documento legal PUBLICADO — la que las compuertas comparan. Distinta de `version`
   * cuando gana el PDF (allí va el `pdf-{guid}`, que es lo que se MUESTRA).
   *
   * La usa el ALTA, que es anónima y no tiene el endpoint de estado del que el modal bloqueante saca
   * la suya. Firmar el `pdf-…` no empataba jamás con la compuerta, y por eso quien acababa de
   * registrarse volvía a ver el modal legal en su primer acceso.
   */
  comparableVersion?: string | null;
}

/**
 * Estado de aceptación legal del usuario (GET v1/legal-acceptances/status). Cuando
 * `requiresAcceptance=true` la app queda bloqueada hasta aceptar las versiones pendientes.
 * Espejo de LegalAcceptanceStatusDto.
 */
export interface LegalAcceptanceStatus {
  requiresAcceptance: boolean;
  /** Documentos cuya versión vigente el usuario aún no ha aceptado. */
  pending: { type: LegalDocType; version: string }[];
  /**
   * OU del usuario, resuelta por el SERVIDOR. Con ella el modal pide el contenido filtrando por OU y
   * no le enseña a nadie el documento de otra geografía.
   *
   * 🔴 NO se sustituye por `currentUser.ouId`: ése sale de `getCurrentUserProfile(email)`, cuyo
   * `.catch` degrada a un perfil sin OU **en silencio**. Y «sin OU» significa «sin filtro», que hoy
   * devuelve las Bases Legales de EUROPA a cualquiera. El servidor ya tiene la OU y no puede
   * perderla.
   */
  ouId?: string | null;
}

/** Cuerpo de POST v1/legal-acceptances — registra la aceptación de una versión concreta. */
export interface LegalAcceptanceBody {
  type: LegalDocType;
  version: string;
  /** Evidencia de lo mostrado (`pdf-{guid}`), o null si se leyó el texto. No es criterio: la
   *  compuerta compara `version`. */
  displayedVersion?: string | null;
}

/** Sección de un documento legal (borrador o publicado) — 7 idiomas por campo. */
export interface LegalDocumentDraftSection {
  heading: Record<string, string>;
  body: Record<string, string>;
  orden: number;
}

/** Borrador editable de un documento legal (GET/PUT .../draft). No exige idiomas completos. */
export interface LegalDocumentDraftDto {
  type: LegalDocType;
  title: Record<string, string>;
  sections: LegalDocumentDraftSection[];
}

/** Cuerpo de PUT .../draft. */
export interface SaveLegalDocumentDraftBody {
  title: Record<string, string>;
  sections: LegalDocumentDraftSection[];
}

/** Cuerpo de POST .../publish. */
export interface PublishLegalDocumentBody {
  version: string;
}

// ─── WP-08 — Reportería de Reconocimiento (AnalyticsService, v1/analytics/recognition/*) ─────────
//
// Espejo de PMP.SharedContracts.DTOs.Recognition (DailyReportDto, CampaignReportDto,
// HistoryReportDto, CampaignLiveKpiDto, HeatmapCellDto, OnaGraphDto, WinnerDto y sus primitivas).
// Serializados por camelCase (JsonSerializerDefaults.Web) como el resto del módulo. SIN puntos
// (regla de oro). `suprimido`/`suprimido=true` señala agregados podados por umbral de anonimato (<5):
// la UI los muestra como "—" o los oculta, nunca inventa el dato.

/**
 * Entrada rankeada reutilizable (top personas, top fundamentales, ranking de campaña).
 * Espejo de RankedEntryDto. `suprimido=true` → fila oculta/enmascarada por anonimato.
 */
export interface RankedEntryDto {
  id: string;
  nombre: string;
  count: number;
  etiqueta?: string | null;
  suprimido?: boolean;

  // ── C2 · las cinco columnas que la maqueta pide en los Top-10 del tab Diario ──
  // Antes no estaban ocultas: NO VIAJABAN. Las llena AnalyticsService (AS #25).

  /** Área/Departamento de la persona, resuelto contra UserAdminService. */
  area?: string | null;

  /** OU de la persona (nombre legible, no el id). */
  ouName?: string | null;

  /** Fundamental PREDOMINANTE de esa persona en el periodo, con desempate estable por id. */
  fundamentalName?: string | null;

  /**
   * ¿ALGUNO de sus reconocimientos cruzó de OU? Son TRES estados, no dos: `true` cruzó, `false`
   * afirma que NO cruza, y `null`/ausente dice que no se sabe porque la OU del otro extremo no se
   * pudo resolver. Colapsarlos a un booleano es justo lo que este campo evita.
   */
  crossOu?: boolean | null;

  /**
   * «Pares» o «Líderes». Sale de `IsManager` de la PERSONA, no del `EsLider` del recibo — ese
   * describe el RECONOCIMIENTO (es true si participa alguien con gente a cargo), así que usarlo
   * clasificaría de «Líderes» a una colaboradora reconocida por su jefa. `null` = sin directorio.
   */
  tipo?: string | null;

  // ── Paridad con ShineRankEntryDto (reporte "Reconocimiento Diario", export de 7 hojas) ──
  // Mismo directorio ya resuelto para area/ouName/tipo, solo faltaba proyectar estos tres.

  /** Correo de la persona. `null` si el directorio no lo tiene o no se pudo resolver. */
  email?: string | null;

  /** Número de nómina, misma degradación que `email`. */
  employeeId?: string | null;

  /** Site/planta de la persona. */
  site?: string | null;
}

/**
 * T-11 — un dia del periodo en la serie del Diario. Espejo de DailyPointDto.
 *
 * Los dias SIN actividad llegan con ceros, no se omiten: la serie es continua para que la
 * sparkline no una dos dias separados y dibuje una subida donde hubo una pausa.
 */
export interface DailyPointDto {
  /** Dia 00:00 UTC (DateTimeOffset ISO). */
  fecha: string;
  enviados: number;
  personasUnicas: number;
  pares: number;
  lideres: number;
}

/**
 * UAT 24-ago-2026 — una de las 4 tarjetas de adopción del tab Diario. Espejo de AdopcionDto.
 *
 * Viajan las DOS cifras y no el porcentaje ya hecho: la tarjeta muestra «72 % · 2 027 de 2 814», que
 * dice mucho más que un 72 % suelto, y sin denominador no se distingue «pocos de muchos» de «pocos
 * de pocos».
 */
export interface AdopcionDto {
  /** Personas del grupo que enviaron al menos un reconocimiento. Solo activas. */
  reconocedores: number;
  /** Tamaño del grupo. **0 = DESCONOCIDO**, no «grupo vacío»: sin censo la tarjeta muestra «—». */
  plantilla: number;
}

/**
 * Bloque de plantilla del armazón de Reportería. Espejo de HeadcountSummaryDto.
 *
 * NO depende del rango de fechas: el censo es una foto del directorio, no una medición del periodo.
 */
export interface HeadcountSummaryDto {
  /** Personas activas del scope. **0 = DESCONOCIDO**, no «organización vacía». */
  total: number;
  frontline: number;
  /** Fecha del último censo (ISO). `null` si no hay snapshot. */
  actualizadoEn?: string | null;
}

/** Etapa de un embudo de participación. Espejo de FunnelStageDto. */
export interface FunnelStageDto {
  etapa: string;
  count: number;
  /** Porcentaje respecto a la etapa base (0-100). */
  pct?: number | null;
  /** De `count`, cuántas personas son Staff ("Administrativos" de la plantilla del cliente). `null`
   *  = el padrón no respondió — nunca se degrada a 0. Bug de producción 2026-08-26. */
  administrativos?: number | null;
  /** De `count`, cuántas personas son Frontline ("Operativos"). Mismo criterio de `null`. */
  operativos?: number | null;
}

/**
 * REQ-077 — Tab "Reconocimiento Diario": semana actual vs previa, embudo y tops.
 * Espejo de DailyReportDto. SIN puntos.
 */
export interface DailyReportDto {
  /**
   * T-05 — total del PERIODO elegido en el filtro (antes `totalSemana`, siempre la semana en curso).
   * Sin rango en el filtro, el backend cae a la semana de referencia.
   */
  totalPeriodo: number;

  /** Total del periodo ANTERIOR EQUIVALENTE: los mismos días inmediatamente antes del elegido. */
  totalPeriodoPrevio: number;
  /** Variación porcentual semana vs previa (-100..+∞). */
  variacionPct: number;
  embudo: FunnelStageDto[];
  topPersonas: RankedEntryDto[];
  topFundamentales: RankedEntryDto[];
  /**
   * Daily Habits del periodo, PLANOS: el SEGUNDO NIVEL del bloque «Distribución por Fundamental».
   * La relación hábito → Fundamental la pone el front con su catálogo — AnalyticsService no lee la
   * base de SurveyService. Aditivo: si falta, el bloque se queda en un solo nivel.
   */
  topHabitos?: RankedEntryDto[] | null;
  /** Alcance del cálculo (OU) o "GLOBAL". */
  scopeId?: string | null;
  suprimido: boolean;
  /** RF-148 — personas distintas que recibieron al menos un reconocimiento esta semana. */
  personasUnicas: number;
  /** RF-148 — reconocimientos donde ni emisor ni receptor tienen gente a su cargo. */
  pares: number;
  /** RF-148 — reconocimientos donde participa al menos una persona con gente a su cargo. */
  lideres: number;
  /** RF-148 — Top 10 personas que más reconocieron. */
  topReconocedores?: RankedEntryDto[] | null;
  /** E5 — primer día 00:00 UTC del mes usado para el Embudo, inclusive (DateTimeOffset ISO). */
  embudoDesde: string;
  /** E5 — último día 00:00 UTC del mes usado para el Embudo, inclusive (DateTimeOffset ISO). */
  embudoHasta: string;
  /**
   * T-11 — un punto por DIA del periodo elegido, para las sparklines de las tarjetas de KPI.
   * Vacía cuando el periodo está suprimido (RN-V05): es un desglose más fino que los totales.
   */
  serie?: DailyPointDto[] | null;
  /**
   * UAT 24-ago — «Usuarios Staff»: de la gente de oficina, cuántas reconocieron EN EL PERIODO.
   * `null` si el padrón no respondió, o si el periodo está suprimido. La tarjeta se calla; NO se
   * pinta 0 %, que afirmaría que nadie de ese grupo reconoció.
   */
  adopcionStaff?: AdopcionDto | null;
  /** UAT 24-ago — «Usuarios Frontline»: lo mismo para el personal de planta. */
  adopcionFrontline?: AdopcionDto | null;
  /**
   * UAT 24-ago — «Usuarios totales»: cuántas han reconocido ALGUNA VEZ. No depende del filtro de
   * fechas y viene precalculada del censo diario. De aquí se deriva «Sin actividad»
   * (`plantilla - reconocedores`), que no viaja como campo propio para que no se desincronice.
   */
  adopcionHistorica?: AdopcionDto | null;
}

/**
 * RF-149 — fila del ranking de "Brillamos Juntos" con OU/BU/Depto resueltos (a diferencia de
 * RankedEntryDto, genérico y reutilizado por Diario/Generacional/Históricos). Espejo de
 * ShineRankEntryDto. Sin Fundamental/Puntos a propósito — ver nota en el DTO backend.
 */
export interface ShineRankEntryDto {
  id: string;
  nombre: string;
  count: number;
  ouName?: string | null;
  buName?: string | null;
  deptName?: string | null;
  suprimido?: boolean;
  /**
   * Columna «Fundamental»: el PREDOMINANTE de esa persona en el recorte. Viaja el ID — lo resuelve el
   * front, que además lo hace en el idioma del usuario y contra el catálogo CON inactivos.
   * Aditivo: puede faltar si el backend no está desplegado, y la celda queda vacía.
   */
  fundamentalId?: string | null;
  /**
   * Columna «Origen»: los canales REALES por los que llegan sus reconocimientos (Diario / Brillamos),
   * no el mayoritario — quedarse con uno ocultaría que alguien recibe por las dos vías.
   */
  origenes?: string[] | null;
  /** Correo de la persona, resuelto contra UserAdminService. Null si el usuario no tiene correo en el directorio. */
  email?: string | null;
  /** Número de nómina, misma fuente y degradación que `email`. */
  employeeId?: string | null;
  /** Área/Departamento (Nivel 4 de la jerarquía). */
  area?: string | null;
  /** Site/planta de la persona. */
  site?: string | null;
  /** Cuántas OUs de receptor DISTINTAS aparecen en los recibos de esta persona en el recorte. Null si no se pudo resolver ninguna. */
  crossOuCount?: number | null;
  /** "Individual" | "Grupal" — el tipo de recibo MÁS FRECUENTE de esta persona en el recorte. Null si no hay recibos de la persona. */
  tipoDominante?: string | null;
}

/** REQ-078 — Tab "Brillamos Juntos": ranking de nominaciones de una campaña. Espejo de CampaignReportDto. */
export interface CampaignReportDto {
  campaignId: string;
  nombre: string;
  totalNominaciones: number;
  nominadosUnicos: number;
  ranking: ShineRankEntryDto[];
  scopeId?: string | null;
  suprimido: boolean;
  /**
   * Qué hábitos se reconocen más. Ya NO es su propia hoja del Excel — desde la Tarea 14b se pliega
   * como segundo nivel (indentado bajo su Fundamental padre) dentro de la hoja «Distribución
   * Fundamental» de `02 - Brillamos Juntos`. Aditivo y opcional — los agregados anteriores a la
   * dimensión de hábitos no lo traen, y una lista vacía es la respuesta honesta («no consta») frente
   * a inventar un ranking.
   *
   * Viaja el ID en `nombre`: el catálogo de hábitos vive en SurveyService y AnalyticsService no lee
   * su base (regla de oro #2). Lo traduce el front con el catálogo que ya carga.
   */
  rankingHabitos?: RankedEntryDto[] | null;
  /**
   * «Distribución por Fundamental» de la maqueta: cuántos reconocimientos usaron cada Fundamental en
   * la campaña. Viaja el id en `id` y también en `nombre` — el catálogo vive en SurveyService y
   * AnalyticsService no lee su base, así que lo traduce el front.
   *
   * Aditivo: puede faltar si el backend todavía no está desplegado, y en ese caso el bloque no se
   * pinta en vez de enseñar una gráfica vacía que se leería como «nadie reconoció por ningún
   * Fundamental».
   */
  topFundamentales?: RankedEntryDto[] | null;
  /**
   * Adopción acotada a ESTA campaña: de la gente del scope, cuántas NOMINARON en ella.
   *
   * 🔴 No son las del Diario. Aquellas son del periodo y del scope; reusarlas habría dado un número
   * correcto bajo un encabezado que dice el nombre de una campaña.
   *
   * `null` = no se pudo calcular (padrón sin responder, o sin censo). NO es 0 %: un cero afirmaría que
   * nadie de ese grupo nominó. «Sin actividad» no viaja — se deriva de `adopcionTotal` por resta.
   */
  adopcionStaff?: AdopcionDto | null;
  adopcionFrontline?: AdopcionDto | null;
  adopcionTotal?: AdopcionDto | null;
  /** RF-148, escala de campaña — reconocimientos donde ni emisor ni receptor tiene gente a su cargo. Null cuando no se pudo calcular (suprimido). */
  pares?: number | null;
  /** Simétrico de `pares`: reconocimientos donde participa al menos un líder. */
  lideres?: number | null;
  /** Reconocimientos de esta campaña con exactamente un receptor. */
  totalIndividual?: number | null;
  /** Reconocimientos de esta campaña con más de un receptor. */
  totalGrupal?: number | null;
  /** Embudo Habituales/Ocasionales/Inactivos de la campaña, mismo denominador que las tarjetas de Adopción (plantilla del scope de quien consulta). Null si no se pudo calcular. */
  embudo?: FunnelStageDto[] | null;
  /** Ranking de quién RECONOCE más dentro de esta campaña (simétrico de `ranking`, que es de quién es reconocido). Mismo tipo de fila, mismas columnas. */
  topReconocedores?: ShineRankEntryDto[] | null;
}

/**
 * RF-150 — Tab "Históricos": histórico completo (Nombre/Nominaciones) de una campaña, acotado a
 * su propia vigencia, no a filtros superiores. Espejo de HistoryReportDto. Reemplaza la serie
 * temporal anterior (HistoryPointDto). Sin columna "Puntos" a propósito — ver nota en el DTO
 * backend (regla de oro #1, pendiente de un endpoint admin-only en RulesEngine-api).
 */
export interface HistoryReportDto {
  campaignId: string;
  personas: RankedEntryDto[];
  suprimido: boolean;
}

/**
 * RF-151 — Reconocimientos/Audiencia de una campaña (Nombre/Tipo/Estado/Fecha de expiración
 * vienen de CampaignDto, no de este DTO). Espejo de CampaignSummaryDto.
 */
export interface CampaignSummaryDto {
  campaignId: string;
  reconocimientos: number;
  audiencia: number;
  suprimido: boolean;

  /**
   * C2 — personas ÚNICAS que ENVIARON al menos un reconocimiento en la campaña (emisores
   * distintos). Es la definición escrita del cliente, hoja «Definiciones» de
   * `03 - Historicos.xlsx`: «Personas distintas que participaron enviando al menos un
   * reconocimiento […] Conteo distinto de usuarios emisores».
   *
   * Es el numerador de las dos columnas calculadas de «Participación por Campaña»:
   *   Tasa         = participantes / audiencia
   *   Prom/usuario = reconocimientos / participantes
   *
   * ⚠️ Sale de los recibos de proyección, que existen desde que la proyección funciona: para una
   * campaña anterior el conteo llega CORTO hasta correr el backfill. Por eso el backend emite 0 y la
   * pantalla pinta «—» en vez de una tasa falsa.
   */
  participantes?: number;

  /**
   * T-10 — personas únicas RECONOCIDAS en esta campaña. Simétrico de `participantes`: ese cuenta
   * quién ENVÍA, este quién RECIBE.
   *
   * ⚠️ No confundir con `CampaignReportDto.nominadosUnicos`, que es del SCOPE y de todos los
   * orígenes. Ese era el «75» que el tab Brillamos mostraba bajo la etiqueta «Personas únicas
   * reconocidas» en el reporte de UAT: un número real midiendo otra cosa.
   */
  nominadosUnicos?: number;

  /**
   * Id del Fundamental con más reconocimientos de esta campaña (mismo recorte de recibos que
   * `participantes`/`nominadosUnicos`). Se usa para pintar la columna "Reconocimientos" de
   * "Participación por Campaña" con el color de ese Fundamental (resuelto contra el catálogo,
   * `useFundamentalsForReporting`). `null`/ausente sin reconocimientos, con supresión por
   * anonimato, o para una campaña anterior a la proyección.
   */
  fundamentalPredominanteId?: string | null;

  /**
   * P18-ter — de `participantes`, cuántos son de oficina (Staff). Columna «Participantes Staff»
   * de la tabla «Campañas Históricas» de `03 - Historicos.xlsx`. Se resuelve con el segmento
   * ACTUAL de cada persona al momento de generar el reporte, no el que tenía cuando participó —
   * no hay forma de reconstruir el segmento histórico. 0 cuando `suprimido` es `true`.
   */
  participantesStaff?: number;

  /** De `participantes`, cuántos son personal de planta (Frontline). Ver `participantesStaff`. */
  participantesFrontline?: number;
}

/**
 * P18-ter — reporte GLOBAL de campañas (todas, no una). Espejo de CampaignsGlobalSummaryDto.
 *
 * A diferencia de `CampaignSummaryDto` (una campaña), estos KPIs son un AGREGADO REAL —no la suma
 * ingenua de `campanas`—: `participantes`/`audienciaTotal` son la UNIÓN de personas/OUs de todas
 * las campañas incluidas, para no contar dos veces a quien participó en más de una, o una OU
 * cubierta por el alcance de dos campañas distintas. `totalReconocimientos` sí es una suma simple
 * (cada reconocimiento pertenece a una sola campaña).
 *
 * Nombre/Descripción/Tipo/Estado/Expiración/Frecuencia/MetaParticipantes de cada fila de
 * `campanas` los resuelve el FRONTEND con el catálogo de campañas ya cargado (mismo criterio que
 * `CampaignSummaryDto`): este DTO solo trae lo que Analytics puede calcular.
 */
export interface CampaignsGlobalSummaryDto {
  totalCampanas: number;
  totalReconocimientos: number;
  audienciaTotal: number;
  participantes: number;
  participantesStaff: number;
  participantesFrontline: number;
  /** true si `participantes` quedó por debajo del umbral de anonimato (los conteos de arriba salen en 0). */
  suprimido: boolean;
  /** Participantes ÷ Audiencia. 0 cuando `suprimido` o sin audiencia. */
  porcentajeParticipacion: number;
  /** Reconocimientos ÷ Participantes. 0 cuando `suprimido` o sin participantes. */
  promedioPorParticipante: number;
  /** Una fila por campaña incluida — mismo cálculo que `getCampaignSummary`/`getCampaignExport` por campaña. */
  campanas: CampaignSummaryDto[];
}

/** REQ-082 — KPIs en vivo de una campaña activa. Espejo de CampaignLiveKpiDto. */
export interface CampaignLiveKpiDto {
  campaignId: string;
  nominaciones: number;
  nominadosUnicos: number;
  ousActivas: number;
  /** Momento del corte (ISO 8601 / DateTimeOffset). */
  asOf: string;
  suprimido: boolean;
  /** RF-154 — días de calendario hasta el cierre de la campaña. Null sin campaña o sin fecha de fin. */
  diasRestantes?: number | null;
}

/** REQ-081 — Celda del mapa de calor OU × Fundamental. Espejo de HeatmapCellDto. */
export interface HeatmapCellDto {
  ouId: string;
  ouName: string;
  fundamentalId: string;
  fundamentalName: string;
  count: number;
  /** Score normalizado 0-1 para el color (madurez). */
  score: number;
  /** true si la celda fue suprimida por umbral de anonimato (<5). */
  suprimido: boolean;
}

/**
 * REQ-088 — Fila de métricas de líderes/OU Ambassadors (fase 1 = tabla + export Excel).
 * Espejo de LeaderMetricRowDto. `ouName` y `embajador` NO vienen resueltos (son el id crudo,
 * ver GetRecognitionLeadersQueryHandler) — se resuelven en el front igual que en Heatmap/Red ONA.
 */
export interface LeaderMetricRowDto {
  ouId: string;
  ouName: string;
  country?: string | null;
  reconocimientos: number;
  participantes: number;
  participacionPct: number;
  embajador?: string | null;
  suprimido: boolean;

  /**
   * C2 — «Equipo» de la maqueta: el censo de la OU.
   */
  tamanoEquipo?: number | null;

  /**
   * C2 — «Recos equipo (recibidos)». El otro lado de `reconocimientos`, que cuenta los ENVIADOS: el
   * agregado se indexa por la OU del emisor. `null` = no se pudo calcular (esa OU no aparece en los
   * recibos, que pueden ser anteriores a la proyección) — nunca 0, que afirmaría que no recibió nada.
   */
  recibidos?: number | null;

  /**
   * C2 — «Nivel». El backend lo manda SIEMPRE en `null`: AnalyticsService no tiene el árbol de OUs.
   *
   * Fase F (Tarea F3, 2026-08-13) — el helper `nivelDeOu` que lo resolvía vivía en `LeadersTab.tsx`
   * y se eliminó junto con su único call site: ese componente se reescribió para la granularidad
   * por-persona del listado agregado de líderes (`LeaderAggregateRowDto`) y ya no consume este DTO
   * OU-based. Este tipo, `getLeaders` y `useRecognitionLeaders` se conservan porque el backend sigue
   * exponiendo el endpoint, pero hoy no tienen ningún consumidor en el front — si se reactiva esta
   * granularidad habrá que resolver «Nivel» de nuevo (client-side contra el árbol de OUs, o
   * portando el cálculo al backend).
   */
  nivel?: number | null;
}

/**
 * Espejo de LeaderAggregateRowDto — Fase F (Tarea F3), listado agregado de líderes (persona, no
 * OU). GET v1/analytics/recognition/leaders-list. `liderNombre`/`ouNombre` YA VIENEN RESUELTOS por
 * el backend (F2a los resuelve vía UserAdminService) — a diferencia de `LeaderMetricRowDto`
 * (OU-based), este DTO no necesita `useOuNameResolver`/`usePersonNameResolver` en el front.
 */
export interface LeaderAggregateRowDto {
  liderId: string;
  liderNombre?: string | null;
  puesto?: string | null;
  ouId?: string | null;
  ouNombre?: string | null;
  /**
   * Fase G (tabla en árbol) — REQ-088. Id del manager DIRECTO del líder (`AppUser.ManagerId`), sin
   * acotar a que también sea "líder" en este listado. `buildLeaderForest` (`leadersTree.ts`) lo usa
   * para reconstruir la jerarquía SOLO entre las filas presentes: si el manager no aparece en el
   * listado, esta fila se pinta como raíz de su propia rama.
   */
  managerId?: string | null;
  /**
   * Profundidad del líder en el organigrama: 1 = raíz (sin manager), sube de 1 en 1 hacia abajo.
   * Calculado en UserAdminService (F2a) como `AncestorIds.Count + 1` sobre TODA la empresa — pinta
   * "N-{n}" en el Excel (`formatNivel` en `LeadersTab.tsx`). `nivelDeOu` (eliminado, ver el
   * comentario de `LeaderMetricRowDto.nivel`) calculaba profundidad de OU, no de PERSONA: no aplica
   * a este dato aunque el nombre del campo coincida con el DTO OU-based.
   *
   * La TABLA en pantalla NO usa este campo para "Nivel": calcula la profundidad SOLO entre los
   * líderes visibles en este listado, armando un árbol con `managerId` (`buildLeaderForest`) — un
   * número que puede diferir de este si el alcance filtrado deja fuera a un jefe intermedio. Este
   * campo (profundidad real, sin acotar a scope) sigue viajando para el Excel.
   */
  nivel?: number | null;
  tamanoEquipo: number;
  recosEquipoRecibidos: number;
  recosEquipoEnviados: number;
  recosQueEnviaElLider: number;
  recosQueRecibeElLider: number;
  participacionPct: number;
  /**
   * `null` cuando `recosEquipoEnviados` es 0 — pinta '—', nunca un 0 falso. Sigue viniendo del
   * backend (alimenta la tarjeta KPI «Ratio promedio»), pero la columna "Ratio" del Excel de
   * Líderes se eliminó a pedido — ver `LeadersTab.tsx`.
   */
  ratio?: number | null;
}

/** Espejo de LeadersListKpiDto — KPIs de Resumen Ejecutivo del listado agregado de líderes. */
export interface LeadersListKpiDto {
  totalLideres: number;
  tamanoEquipoSuma: number;
  /** `null` si la suma de enviados es 0. */
  ratioPromedio?: number | null;
  participacionPromedio: number;
  /** Suma de `recosEquipoRecibidos` de TODAS las filas — fila "Recos equipo (recibidos)" de "06 - Lideres.xlsx". */
  recosEquipoRecibidosSuma: number;
  /** Suma de `recosEquipoEnviados` de TODAS las filas. */
  recosEquipoEnviadosSuma: number;
  /** Suma de `recosQueRecibeElLider` de TODAS las filas (los líderes mismos, sin su equipo). */
  recosQueRecibeElLiderSuma: number;
  /** Suma de `recosQueEnviaElLider` de TODAS las filas (los líderes mismos, sin su equipo). */
  recosQueEnviaElLiderSuma: number;
}

/** Espejo de LeadersListResponseDto (GET v1/analytics/recognition/leaders-list). */
export interface LeadersListResponseDto {
  lideres: LeaderAggregateRowDto[];
  kpis: LeadersListKpiDto;
}

/** Nodo del grafo ONA (una OU). Espejo de OnaNodeDto. */
export interface OnaNodeDto {
  ouId: string;
  ouName: string;
  country?: string | null;
  /** Grado ponderado (suma de reconocimientos que tocan la OU). */
  peso: number;
}

/** Arista dirigida del grafo ONA (flujo emisor-OU → receptor-OU). Espejo de OnaEdgeDto. */
export interface OnaEdgeDto {
  sourceOuId: string;
  targetOuId: string;
  peso: number;
  /** true si el flujo cruza OU/país (colaboración cruzada). */
  crossOu: boolean;
  /**
   * C2/C4 (2026-08-13) — Fundamental más reconocido en esta relación cross-OU. Reemplaza el
   * indicador de "Intensidad" 0-100% que la spec descartó (sección 4.8). Se resuelve del bucket
   * RECEPTOR de la relación (`GetRecognitionNetworkQueryHandler`); `null` sin datos suficientes.
   * Es un id de Fundamental — resolver nombre/color vía el catálogo (`useRecognitionCatalog` +
   * `getFundamentalName`/`getFundamentalColor`), igual que el resto de la Reportería.
   */
  fundamentalDominante: string | null;
}

/** REQ-081 — Grafo ONA de reconocimiento cross-OU. Espejo de OnaGraphDto. */
export interface OnaGraphDto {
  nodos: OnaNodeDto[];
  aristas: OnaEdgeDto[];
  /** true si el grafo fue podado por umbral de anonimato. */
  suprimido: boolean;
}

/**
 * Ganador de una campaña (carga manual/CSV con insignia). Export en 2 formatos por cascadeo
 * (raw | cascaded), NUNCA top global (CA-046 / WP-08). Espejo de WinnerDto.
 */
export interface WinnerDto {
  id: string;
  campaignId: string;
  userId: string;
  nombre: string;
  ouId?: string | null;
  ouName?: string | null;
  country?: string | null;
  badgeId?: string | null;
  /** Posición dentro de su nivel de cascadeo (1 = primer lugar). */
  posicion?: number | null;
  /** Origen cruzado (promote diario→Brillamos, CA-046). */
  origenCruzado: boolean;
  /**
   * RF-130 M1 (H-15) — REQUERIDO, no opcional/anulable: el backend lo emite SIEMPRE.
   * `Winner.RegistradoAt` (dominio) es `DateTimeOffset` no anulable y `Winner.Create` lo fija a
   * `DateTimeOffset.UtcNow` cuando no se captura uno explícito (`Winner.cs`); `WinnerMapper.ToDto`
   * lo copia tal cual. (El contrato de SharedContracts declara el parámetro del record como
   * `DateTimeOffset?` por compatibilidad de firma, pero el dominio nunca deja el campo vacío — el
   * tipo aquí sigue lo que el backend REALMENTE entrega, no la laxitud de la firma del constructor.)
   * Un tipo más laxo que esto habilitaba una rama muerta en `WrappedModal.findMyWinner` que trataba
   * la ausencia del dato como si fuera un ganador sin fecha resoluble — un caso que nunca ocurre.
   */
  registradoAt: string;
  /**
   * AÑO DEL PREMIO — lo que hay que usar para ubicar a un ganador en un año, NO `registradoAt`, que
   * es cuándo lo dieron de alta. El backend ya lo publicaba en la tabla del admin (`WinnerRowDto`);
   * en este DTO faltaba, así que el Wrapped tenía que deducirlo de la fecha de registro y desubicaba
   * a todo ganador dado de alta fuera de su año — y a los históricos importados en bloque, que
   * comparten fecha de importación.
   *
   * Opcional a propósito: los ganadores anteriores a WP-D1 no lo traen. Ver el respaldo explícito en
   * `WrappedModal.findMyWinner`.
   */
  anio?: number | null;
  /**
   * Bug #87 — sin este campo, `WrappedModal.findMyWinner` no podía distinguir un ganador Pendiente
   * (recién importado por CSV, sin confirmar) de uno Confirmado, y anunciaba la insignia antes de
   * tiempo. Opcional porque los ganadores anteriores a CPE-134 no lo traen; `undefined`/`null` se
   * trata como Confirmado (el default del dominio), así que solo se excluye explícitamente
   * `'Pendiente'`.
   */
  estado?: WinnerEstado | null;
}

/** Estado de un ganador en la tabla del admin (CPE-134). */
export type WinnerEstado = 'Pendiente' | 'Confirmado';

/**
 * RF-157 / WP-D1 — una fila del listado del administrador de Ganadores.
 *
 * 🔴 NO trae puntos, y no es un olvido: `Winner` tiene prohibido almacenarlos (REQ-040) y el dato
 * vive en el libro de RulesEngine. La columna de la maqueta se resuelve aparte (D-5); mientras no
 * haya vía, va OCULTA — no en cero, que sería afirmar que valen 0.
 */
export interface WinnerRowDto {
  id: string;
  campaignId: string | null;
  userId: string;
  nombre: string;
  anio?: number | null;
  ouId?: string | null;
  ouName?: string | null;
  country?: string | null;
  /** Área / unidad de negocio (columna "Área" de la maqueta, filtro BU de RF-157). */
  area?: string | null;
  /** Planta (columna "Site"). */
  siteName?: string | null;
  fundamentalId?: string | null;
  badgeId?: string | null;
  posicion?: number | null;
  estado: WinnerEstado;
  registradoAt: string;
  /**
   * 🔴 Si la ventana de edición sigue abierta para ESTA fila, resuelto en BACKEND.
   *
   * Viaja ya decidido porque la regla depende del rol de quien pregunta y del año del premio;
   * recalcularla en el cliente sería duplicar una regla de negocio y arriesgarse a que las dos
   * versiones divergan — con la del cliente ganando visualmente y la del servidor devolviendo 403.
   */
  editable: boolean;
  /**
   * Correo del ganador. La maqueta lo pinta bajo el nombre en la celda «Usuario»
   * (`fundamentales-admin.js:341`) y desde D-10 es además la LLAVE de la carga por fichero.
   *
   * No lo persiste `Winner`: el backend lo resuelve contra el directorio al servir la página. Con
   * `null` —usuario borrado, o ficha Frontline sin correo— la celda cae al `userId`, que es lo que
   * se pintaba antes.
   */
  email?: string | null;

  /**
   * Número de nómina, SOLO resuelto por el backend cuando la fila no tiene correo (ficha Frontline
   * sin correo). Reemplaza al `userId` crudo —un id interno que el administrador no reconoce— como
   * fallback bajo el nombre en la celda «Usuario»: ver `AdminWinners.tsx`. `null` si tampoco hay
   * nómina en el directorio, caso en el que la celda sigue cayendo al `userId`.
   */
  nomina?: string | null;
}

/** Una página del listado de Ganadores. */
export interface WinnersPageDto {
  filas: WinnerRowDto[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Task 4.5 — espejo EXACTO de `NominationRawExportRowDto` (SharedContracts, Task 4.3/4.4): una fila
 * del export CRUDO de nominaciones (spec §4.1, calcado de `08 - Ganadores.xlsx`).
 *
 * 🔴 Una fila por CANDIDATO: una nominación grupal con 3 receptores produce 3 filas repitiendo los
 * bloques `nominee*`/`onBehalfOf*`. No se aplana distinto en el cliente — es el aplanado que el
 * comité espera para filtrar/ordenar en una tabla dinámica de Excel.
 *
 * 🔴 `habitos` es DINÁMICO (`IReadOnlyDictionary<string,bool>` en C#), clave = código del hábito. NO
 * es una lista fija de columnas: el libro de origen tenía 32 porque esa era la foto del catálogo al
 * generarlo, no un tope de negocio. Cada fila puede traer un juego de claves distinto si el catálogo
 * cambió entre nominaciones.
 */
export interface NominationRawExportRowDto {
  nomineeName: string;
  nomineeEmail?: string | null;
  nomineeEmployeeId?: string | null;
  nomineeOu?: string | null;
  nomineeBu?: string | null;
  nomineeSite?: string | null;
  nomineeDepartment?: string | null;
  onBehalfOfName?: string | null;
  onBehalfOfEmail?: string | null;
  onBehalfOfEmployeeId?: string | null;
  onBehalfOfOu?: string | null;
  onBehalfOfBu?: string | null;
  onBehalfOfSite?: string | null;
  onBehalfOfDepartment?: string | null;
  candidateName: string;
  candidateEmail?: string | null;
  candidateEmployeeId?: string | null;
  candidateOu?: string | null;
  candidateBu?: string | null;
  candidateSite?: string | null;
  candidateDepartment?: string | null;
  /** `DateTimeOffset` serializado (ISO 8601, UTC). */
  date: string;
  anio: number;
  mes: string;
  /** Clave = código del hábito (catálogo dinámico), valor = si el nominador lo marcó. */
  habitos: Readonly<Record<string, boolean>>;
  comment?: string | null;
  badgeDailyStreak?: string | null;
  fromDayToDayCampaign: boolean;
  /** "Confirmado" | "Pendiente" | "Desestimado" | "Resuelto" — ya viene resuelto del backend. */
  status: string;
  /** "Individual" | "Grupal". */
  tipo: string;
  audienceNominee?: string | null;
  audienceCandidate?: string | null;
  /** true si el reconocimiento entró por carga masiva CSV (tiene ExternalId). */
  migrated: boolean;
  /** true si ALGUIEN reportó esta nominación. */
  sendingAReport: boolean;
  /** true si algún reporte sobre esta nominación PROSPERÓ (moderación resuelta). */
  receivedAReport: boolean;
  /** "Líder" | "Colaborador", resuelto contra IsManager actual. Null si no se pudo resolver. */
  nomineeRole?: string | null;
  candidateRole?: string | null;
  /** Puntos que RulesEngine acreditó a esta nominación. Null si no aplica (OU sin puntos, diario, o
   *  RulesEngine no respondió). Nunca se confunde con 0, que es un valor legítimo. */
  puntos?: number | null;
}

/**
 * Task 4.5 — espejo de `NominationsRawExportPageDto`.
 *
 * 🔴 `total` es el de NOMINACIONES, no el de filas: no sirve para predecir cuántas páginas hacen
 * falta (una nominación grupal aporta varias filas). El bucle de paginado debe cortar en página
 * vacía, igual que `exportar()` — nunca en `filas.length * pageSize >= total`.
 */
export interface NominationsRawExportPageDto {
  filas: NominationRawExportRowDto[];
  total: number;
  page: number;
  pageSize: number;
}

/** Filtros de `GET v1/winners/search` (RF-157). Todo se aplica en SERVIDOR. */
export interface WinnerSearchParams {
  q?: string;
  anio?: number;
  campaignId?: string;
  ouId?: string;
  country?: string;
  buId?: string;
  fundamentalId?: string;
  /**
   * CPE-134 — «Pendiente» | «Confirmado». El backend lo implementa y el contrato lo declara; el
   * espejo no lo tenía, así que era un filtro inalcanzable desde la pantalla — el mismo patrón que
   * el propio código ya señalaba para `buId`.
   */
  estado?: WinnerEstado;
  page?: number;
  pageSize?: number;
}

/** Formato del export de ganadores (winners-export?format=). */
export type WinnersExportFormat = 'raw' | 'cascaded';

/**
 * Filtros comunes de la reportería (rango + OU + fundamental) que viajan como query-string a los
 * endpoints v1/analytics/recognition/*. `campaignId` acota la campaña; ausente → el backend usa la
 * campaña activa del alcance. `language` localiza los nombres agregados.
 */
/**
 * Maqueta del tab Brillamos — select «Filtro Brillamos». Acota el ranking de nominados.
 *
 * `personaUnica`: solo reconocimientos con UN destinatario. La definición es del cliente, hoja
 * «Definiciones» de `01 - Diario.xlsx`: «un grupo es aquel que tiene más de un destinatario».
 * `crossOu`: solo los que cruzan de OU. Una OU de receptor desconocida NO cuenta como cruce.
 *
 * ⚠️ Este filtro sale solo del prototipo, no de la Sección 7 de la Especificación.
 */
export type RecognitionModalidad = 'todos' | 'personaUnica' | 'crossOu';

export interface RecognitionReportParams {
  ou?: string;
  /**
   * Fase 4 del plan de Reportería — selección múltiple del filtro de OU: lista YA EXPANDIDA con los
   * descendientes de cada OU elegida (mismo patrón que `AnalyticsFilterParams.ouIds` del módulo
   * Analytics genérico). El backend la recibe como `scopeIds` (CSV) y la combina con `ou`/`scope`
   * sin duplicar. `ou` se mantiene por compatibilidad con quien construya el objeto con una sola OU.
   */
  ouIds?: readonly string[];
  startDate?: string;
  endDate?: string;
  fundamentalId?: string;
  /**
   * Persona elegida en la tira de Reportería (su **id**, no su nombre).
   *
   * 🔴 Viaja el id y no el texto tecleado a propósito: el buscador es un combobox de selección
   * estricta, así que quien filtra ya eligió a una persona concreta del directorio. Antes esto era un
   * `searchQuery` de texto que se comparaba en el cliente contra el nombre ya resuelto, y fallaba en
   * los tres casos que se ven a diario: mientras el nombre no se resolvía (era el GUID), cuando el
   * directorio devuelve «Apellidos, Nombre» y quien busca escribe el nombre de pila, y cuando la
   * persona no entra en el Top que se pidió.
   *
   * Se declara aquí (Task 1, 2026-09-03) porque `filtrosActivosDe`/`soloPersonaDe`
   * (`reportsShared.tsx`) ya lo necesitan para tipar qué avisa cada sección; la propagación real a
   * `toReportRangeQuery`/`toDailyQuery`/`toCampaignQuery` llega en un paso posterior del mismo plan.
   */
  personaId?: string;
  campaignId?: string;
  language?: string;
  /**
   * BU / Depto / Site del padrón. Texto libre, sin catálogo maestro — el backend compara sin
   * distinguir mayúsculas ni espacios de sobra.
   *
   * 🔴 La tira global de Reportería YA NO los expone (retirados 2026-09-02 — ver
   * `docs/contexto/REPORTERIA-REGLAS.md`, sección BU/Site/Depto). Solo siguen vivos vía el filtro
   * LOCAL propio del tab Demográfico (RF-152, `GenerationalTab.tsx`), que arma su propio
   * `RecognitionReportParams` con estos campos — nada más los llena. `GetRecognitionDailyQuery`
   * conserva el parámetro por compatibilidad de contrato, pero nada del frontend se lo manda ya.
   */
  bu?: string;
  depto?: string;
  site?: string;
  /** Solo lo consume el tab Brillamos. Ausente u `todos` ⇒ el ranking no se acota. */
  modalidad?: RecognitionModalidad;
  /**
   * Filas a pedir en los Top. Ausente ⇒ el valor por omisión del backend (10 en el Diario, 50 en el
   * ranking de campaña). Techo 200, aplicado en el handler.
   */
  top?: number;
}

/**
 * E5 — filtro propio de año/mes del Embudo de Adopción (tab Diario), independiente del
 * `reference`/`scope` que ya manda `RecognitionReportParams`. Exclusivo de ese bloque: NO afecta a
 * los KPIs de semana actual/previa de arriba, que siguen anclados a `reference`.
 */
export interface DailyFunnelFilter {
  anio?: number;
  /** 1-12. */
  mes?: number;
}

/**
 * RF-152 — filtros propios del tab Generacional (BU/Depto/Site en texto libre, sin catálogo
 * maestro — igual que UserDto.OrganizationalDirection/Department/SiteId no tienen tabla propia).
 * Independientes del FilterBar compartido: la OU/fecha siguen viniendo de RecognitionReportParams.
 */
export interface GenerationalFilters {
  bu?: string;
  depto?: string;
  site?: string;
  /** 'lideres' (UserDto.IsManager=true) | 'pares' (IsManager=false) | undefined = todos. */
  lideresOPares?: 'lideres' | 'pares';
}

/**
 * C5/C7 (2026-08-13, BREAKING) — respuesta de GET v1/analytics/recognition/generational. Antes el
 * endpoint devolvía un array plano de `RankedEntryDto`; ahora envuelve la distribución junto con la
 * cobertura geográfica del cálculo. Espejo EXACTO de GenerationalReportDto (camelCase).
 */
export interface GenerationalReportDto {
  /** Distribución de reconocimientos por banda generacional (Etiqueta="generation"). */
  bandas: RankedEntryDto[];
  /**
   * IDs de OU (NO nombres legibles) cuya cobertura de fecha de nacimiento fue suficiente para
   * aportar al menos una persona con banda generacional resuelta. Resolver el nombre vía el
   * catálogo de OUs (`useOuNameResolver`), igual que el resto de la Reportería. Una OU ausente de
   * esta lista no significa "sin actividad": significa que su gente muestreada no tenía fecha de
   * nacimiento confiable.
   */
  ousConCobertura: string[];
}

/**
 * Entrada de distribución generacional para censo de plantilla activa (no actividad de periodo).
 * Espejo EXACTO de GenerationalDistributionEntryDto (camelCase) — ver Task 5/Excel generacional.
 */
export interface GenerationalDistributionEntryDto {
  generation: string;
  total: number;
  frontline: number;
  staff: number;
  porOu: Record<string, number>;
  suprimido: boolean;
}

/**
 * Distribución de plantilla activa por banda generacional (censo, no actividad).
 * Respuesta de GET v1/analytics/recognition/generational-distribution.
 * Espejo EXACTO de GenerationalDistributionReportDto (camelCase).
 */
export interface GenerationalDistributionReportDto {
  bandas: GenerationalDistributionEntryDto[];
  ous: string[];
}

// ─── REQ-124 — Vista de equipo (líder) — v1/analytics/recognition/team ────────────

/** Métrica de un miembro del equipo (SIN puntos). Espejo de TeamMemberMetricDto (camelCase). */
export interface TeamMemberMetricDto {
  userId: string;
  nombre: string;
  enviados: number;
  recibidos: number;
  /** Estado de racha legible (ej: "activa", "en riesgo"). null si no aplica. SIN puntos. */
  rachaEstado?: string | null;
  /**
   * Id del Fundamental por el que MÁS RECONOCEN a este miembro (resolver el nombre vía catálogo).
   *
   * 🔴 Desde el 18-ago-2026 este campo mide lo RECIBIDO, que es lo que su documentación en
   * SharedContracts dijo siempre. Antes devolvía lo que ahora viaja en `topFundamentalEnviadoId`, y
   * como esa distribución solo se escribe en el documento del emisor, quien recibía reconocimientos
   * sin enviar ninguno salía en «—» (reporte de UAT).
   */
  topFundamentalId?: string | null;
  /** true si este miembro es a su vez manager de otro equipo (habilita drill-down). Task 12. */
  isManager: boolean;
  /**
   * Id del Fundamental que este miembro más USA al reconocer a otros. Aditivo (18-ago-2026): puede
   * faltar si el backend todavía no está desplegado, y en ese caso la fila enseña solo lo recibido.
   */
  topFundamentalEnviadoId?: string | null;
}

/**
 * Métricas de equipo del líder (árbol manager → reportes directos). Espejo EXACTO de
 * PMP.SharedContracts.DTOs.Recognition.TeamMetricsDto (camelCase). NUNCA expone puntos individuales.
 */
export interface TeamMetricsDto {
  managerId: string;
  managerName: string;
  teamSize: number;
  enviadosEquipo: number;
  recibidosEquipo: number;
  /** % de miembros con al menos un reconocimiento (0-100). */
  participacionPct: number;
  miembros: TeamMemberMetricDto[];
  /** true si algún agregado fue suprimido por umbral de anonimato. */
  suprimido: boolean;
}

/** Parámetros de GET v1/analytics/recognition/team (managerId opcional solo si el caller es global). */
export interface RecognitionTeamParams {
  managerId?: string;
  from?: string;
  to?: string;
}

// ─── REQ-125 — Tablero ejecutivo K-01..K-12 — v1/analytics/recognition/summary ────

/**
 * KPI del tablero ejecutivo (K-01..K-10, +K-11/K-12 con includeInactivity). Espejo EXACTO de
 * PMP.SharedContracts.DTOs.Recognition.RecognitionKpiDto (camelCase). `suprimido=true` → la UI
 * muestra "—" (umbral de anonimato Europa <5). Nunca expone puntos individuales.
 */
export interface RecognitionKpiDto {
  /** Clave del KPI ("K-01"..."K-12"). */
  key: string;
  nombre: string;
  valor: number;
  /** Unidad: "count" | "percent" | "ratio". */
  unidad: string;
  /** Alcance del cálculo (OU/global). */
  scopeId?: string | null;
  suprimido: boolean;
  /**
   * RF-143 — el KPI no se puede calcular CON EL FILTRO PUESTO. No es lo mismo que `suprimido`: ahí
   * el dato existe y se tapa por anonimato; aquí no existe.
   *
   * El caso real: filtrando por generación, el censo (`OrgHeadcountSnapshot`) sigue siendo la
   * plantilla entera —no se desglosa por generación—, así que las tasas que dividen por ella darían
   * «% de TODA la plantilla que es emisora de esa generación» y se leerían como «participación de
   * esa generación». Se pinta un hueco, no un número.
   */
  noAplicaConFiltro?: boolean;
}

/** Parámetros de GET v1/analytics/recognition/summary. `scope` acota la OU; sin scope = alcance del rol. */
export interface RecognitionSummaryParams {
  scope?: string;
  from?: string;
  to?: string;
  /** REQ-125 — añade K-11 (% nunca entraron) y K-12 (% entraron sin actividad). */
  includeInactivity?: boolean;
  /**
   * RF-143 — drill-down por Fundamental. El id es el del catálogo único de SurveyService
   * (`useRecognitionCatalog`), que es EXACTAMENTE el que un reconocimiento guarda en
   * `fundamentalIds`. Ojo: NO es el `fundFilter` de la barra de analítica, que selecciona
   * COMPORTAMIENTOS cuando el Fundamental los tiene — ese id no existe en un reconocimiento y
   * devolvería cero en silencio.
   */
  fundamentalId?: string;
  /**
   * RF-143 — drill-down por banda generacional DEL EMISOR. Los valores son los que calcula
   * `GenerationHelper` en UserAdminService y persiste en `AppUser.Generation`:
   * `GenAlpha | GenZ | Millennial | GenX | BabyBoomer | Silent`. Cualquier otra etiqueta no casa
   * con nadie y el tablero saldría en cero.
   */
  generacion?: string;
}

// ─── FASE 4 — Documentation Pills del rail (DocumentationPillsController) ─────────
// El catálogo de Fundamentales de RecognitionService (v1/recognition-catalog) se retiró (P2-010):
// el catálogo único vive en SurveyService. Aquí quedan solo los tipos de Documentation Pills.

/** Tipo de destino de una pill de acceso rápido (CPE-164): documento corporativo | URL | video. */
export type PillContentType = 'documento' | 'url' | 'video';

/**
 * "Pill" de acceso rápido del rail (CPE-162/163/164). Espejo de DocumentationPillDto (backend). De tipo
 * flexible: `documento` resuelve el PDF por `targetDocumentId` (contra corporate-documents/for-me, que
 * re-firma el SAS); `url`/`video` apuntan a `url`.
 */
export interface DocumentationPillDto {
  id: string;
  titulo: LocalizedText;
  tipo: PillContentType;
  icono: string;
  color: string;
  orden: number;
  scopeOuIds: string[];
  isActive: boolean;
  targetDocumentId?: string | null;
  url?: string | null;
}

/** Cuerpo de POST/PUT v1/documentation-pills. Espejo de CreatePillBody/UpdatePillBody. */
export interface UpsertPillBody {
  titulo: LocalizedText;
  tipo: PillContentType;
  icono: string;
  color: string;
  orden: number;
  scopeOuIds: string[];
  isActive: boolean;
  targetDocumentId?: string | null;
  url?: string | null;
}

// ─── WP-15 REQ-130 — Moderación (bandeja de reportes + palabras bloqueadas) ──────

/** Parte involucrada en un reporte (reportante o reportado). NUNCA anónimo. Espejo de ReportPartyDto. */
export interface ReportPartyDto {
  userId: string;
  nombre: string;
  correo?: string | null;
  ouName?: string | null;
  /** Número de nómina (frontline) cuando aplica. */
  nomina?: string | null;
}

/** Receptor del reconocimiento reportado (del snapshot embebido). Espejo de ReportRecipientDto. */
export interface ReportRecipientDto {
  userId: string;
  nombre: string;
  /** Null cuando sale del snapshot: OrgSnapshot no guarda correo. */
  correo?: string | null;
}

/** Tipo de reconocimiento del maquetado, derivado en el backend de `origen` + campaña. */
export type ReportRecoType = 'fundamentales' | 'brillamos' | 'diario';

/**
 * Contexto del reconocimiento reportado que el backend proyecta sobre el reporte. NO se persiste en
 * `reports`: sale de `Recognition` + sus snapshots. Es lo que retira las N peticiones
 * `GET v1/recognitions/{id}` que la bandeja hacía, una por fila.
 */
export interface ReportRecognitionContext {
  /** Columna "Para". */
  receptores?: ReportRecipientDto[] | null;
  /** Filtro de tipo de la bandeja. */
  tipo?: ReportRecoType | null;
  /** CLAVE de la OU (no la etiqueta) — es contra lo que filtra la bandeja. */
  ouId?: string | null;
  ouName?: string | null;
  /** Null mientras UserAdminService no exponga BU/Site. */
  buName?: string | null;
  siteName?: string | null;
  country?: string | null;
}

/** Reporte de moderación sobre un reconocimiento. Espejo de ReportDto. */
export interface ReportDto extends ReportRecognitionContext {
  id: string;
  reconocimientoId: string;
  motivo: string;
  reportante: ReportPartyDto;
  reportado: ReportPartyDto;
  estado: ModerationStatus;
  fecha: string;
  /** Columna "Detalle": RESUMEN del mensaje. El íntegro va en `ReportDetailDto.mensaje`. */
  mensajePreview?: string | null;
  /** G1 — idioma derivado del país del reconocimiento reportado (ver backend). Null si no resuelve. */
  idioma?: string | null;
  /** G1 — espejo de Recognition.ReportCount al momento de la consulta. */
  numeroReportes?: number | null;
}

/**
 * Reportería de Moderación (WP-08, Fase 3): KPIs agregados + detalle de casos. Espejo de
 * ModerationReportDto. Reutiliza `ReportDto` tal cual — ya trae contacto/motivo/estado resueltos,
 * sin ids crudos que resolver en el front (a diferencia del resto de tabs de Reportería).
 *
 * SIN campo `suprimido`: este reporte no aplica el umbral de anonimato (decisión de negocio
 * 2026-09-04). Es el único tab de Reportería nominal por diseño —lista nombre y correo de las dos
 * partes para escalar el caso— y sirve las mismas filas que la bandeja de moderación, que nunca
 * las podó.
 */
export interface ModerationReportDto {
  total: number;
  pendientes: number;
  ocultos: number;
  restaurados: number;
  casos: ReportDto[];
}

/** Detalle de un reporte con datos de canalización (REQ-052). Espejo de ReportDetailDto. */
export interface ReportDetailDto extends ReportRecognitionContext {
  id: string;
  reconocimientoId: string;
  motivo: string;
  reportante: ReportPartyDto;
  reportado: ReportPartyDto;
  estado: ModerationStatus;
  fecha: string;
  /** Texto ÍNTEGRO del reconocimiento reportado (contexto del caso). */
  mensaje?: string | null;
  /** Fundamentales etiquetados en el reconocimiento reportado. */
  fundamentales?: string[] | null;
}

/**
 * RF-090 — códigos de motivo de la previsualización del ✉️. Espejo de `ModerationNotifyReasons`
 * (SharedContracts). Son códigos, no textos: la pantalla los traduce a los 7 idiomas.
 */
export type ModerationNotifyReason =
  | 'sin-admins-en-la-ou'
  | 'escalado-a-globales'
  | 'admin-sin-correo'
  | 'directorio-no-responde'
  | 'suprimido-por-allowlist'
  | 'allowlist-desconocida';

/** Un destinatario resuelto del aviso de moderación. Espejo de ModerationNotifyRecipientDto. */
export interface ModerationNotifyRecipientDto {
  userId: string;
  /** Nombre para mostrar. `null` si el directorio no lo trae — no se inventa. */
  name?: string | null;
  /** Correo resuelto. `null` si no se pudo resolver. */
  email?: string | null;
  /** true si el aviso le llegaría de verdad. */
  deliverable: boolean;
  /** Motivo por el que NO le llegaría. `null` cuando `deliverable` es true. */
  blocked?: ModerationNotifyReason | null;
}

/**
 * RF-090 — a quién le llegaría el ✉️ ANTES de pulsarlo. Espejo de ModerationNotifyPreviewDto.
 *
 * Existe porque el POST devolvía `success: true` en cuanto el evento entraba al bus: cuanto podía
 * salir mal ocurría después, en NotificationService, y el moderador veía el icono en verde dando el
 * caso por escalado sin que lo recibiera nadie.
 */
export interface ModerationNotifyPreviewDto {
  ouId?: string | null;
  country?: string | null;
  /** true si no hay admin acotado y se recurre a los globales. */
  escalated: boolean;
  recipients: ModerationNotifyRecipientDto[];
  /** true si hay al menos un destinatario entregable: la condición para poder enviar. */
  canNotify: boolean;
  /** Motivo por el que no se puede enviar. `null` cuando `canNotify` es true. */
  reason?: ModerationNotifyReason | null;
  /** Códigos informativos que NO impiden el envío. */
  warnings?: ModerationNotifyReason[] | null;
}

/** Palabra bloqueada del catálogo de moderación, por país/idioma. Espejo de BlockedWordDto. */
export interface BlockedWordDto {
  id: string;
  word: string;
  /** Código de país (ISO). "*" = todos. */
  country: string;
  /** Código de idioma (es/en/de/pt/nl/fr/ro). "*" = todos. */
  languageCode: string;
  isActive: boolean;
}

/**
 * RF-082 — tabla configurable país → idiomas hablados. Espejo de `CountryLanguageMapDto`.
 *
 * Gobierna qué catálogo de palabras aplica a cada emisor y alimenta los selectores del gestor: sin
 * ella habría que capturar cada palabra una vez por idioma en los países multilingües (Bélgica).
 * Países en ISO-3166 alpha-2; idiomas en BCP-47 (`es-MX`); regiones con prefijo `@` (`@LATAM`).
 */
export interface CountryLanguageMapDto {
  /** País ISO → idiomas hablados. El primero es el principal. */
  countries: Record<string, string[]>;
  /** Región (`@LATAM`, `@EU`) → países ISO que la componen. */
  regions: Record<string, string[]>;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

/** Cuerpo de POST v1/moderation/blocked-words. Espejo de BlockedWordBody. */
export interface BlockedWordBody {
  word: string;
  country?: string;
  languageCode?: string;
}

/**
 * Toggles de moderación (view_settings, REQ-053). El backend SIEMPRE revalida al crear un reporte
 * (regla de oro #3). Espejo de ModerationSettingsDto.
 */
export interface ModerationSettingsDto {
  /** Apagado global de moderación: si es false, no se aceptan reportes en ninguna OU. */
  globalEnabled: boolean;
  /** Default global de "reportar ON/OFF" cuando la OU no tiene override. */
  reportEnabledGlobal: boolean;
  /** Límite diario de reportes por usuario (~5). */
  dailyReportLimit: number;
  /**
   * Cuántos reportes ocultan automáticamente un reconocimiento (RF-085). Por omisión **1** —el
   * comportamiento histórico— y no 3 como muestra el maquetado: subirlo es decisión del admin (DP-1).
   */
  hideThreshold: number;
  /**
   * Avisar al reportador cuando su reporte se resuelve. Aquí solo se persiste la preferencia y viaja
   * en el evento de moderación; el envío del correo es de NotificationService y todavía no existe.
   */
  notifyReporter: boolean;
  /** Overrides por OU: ouId → reportar habilitado. */
  reportEnabledByOu?: Record<string, boolean> | null;
  /** Valor efectivo de "reportar" para el ouId consultado (resolución puntual). */
  resolvedReportEnabled?: boolean | null;
  /**
   * Bloqueo automático con IA (REQ-055): con este toggle —y globalEnabled— activos, cada mensaje pasa
   * por Azure AI Content Safety al enviarse y se rechaza si es ofensivo. Aditivo al catálogo de palabras.
   */
  autoAiBlocking?: boolean;
}

// ─── WP-15 REQ-131 — Ganadores (WinnersController) ───────────────────────────────

/** Cuerpo de POST v1/winners — registra/actualiza un ganador (idempotente por campaña+usuario). Espejo de RegisterWinnerBody. */
export interface RegisterWinnerBody {
  campaignId: string | null;
  userId: string;
  nombre: string;
  ouId?: string | null;
  ouName?: string | null;
  country?: string | null;
  badgeId?: string | null;
  posicion?: number | null;
  origenCruzado?: boolean;

  /*
   * WP-D1 — los cinco de la maqueta. El backend los transporta desde `fe98a21`, pero el espejo no
   * los tenía, así que el formulario de edición seguía sin poder corregir un año mal cargado: el
   * defecto que ese commit dice cerrar seguía abierto de extremo a extremo. Lo vio la revisión
   * adversarial.
   */
  anio?: number | null;
  fundamentalId?: string | null;
  area?: string | null;
  siteId?: string | null;
  siteName?: string | null;
}

/**
 * Cuerpo de POST v1/winners/import (multipart/form-data). Espejo de los parámetros de
 * `WinnersController.Import`. `campaignId` obligatorio SOLO en modo "vigente"; `badgeId`
 * obligatorio SOLO en modo "historica" — la insignia se elige una vez por carga, no por fila.
 */
export interface ImportWinnersBody {
  file: File;
  modo: 'vigente' | 'historica';
  campaignId?: string;
  badgeId?: string;
  /** Validación previa: corre toda la validación real contra el backend sin escribir nada.
   *  Espejo de `ImportWinnersCsvCommand.Preview`. */
  preview?: boolean;
}

/** Error de una fila (o del archivo/selección completa, con `fila: 0`) de la importación de
 *  ganadores. Espejo de WinnerImportErrorDto. */
export interface WinnerImportErrorDto {
  /** Número de fila (1-based, sin cabecera). `0` = error de archivo/selección completa (cabecera
   *  inválida, campaña o insignia sin elegir o no encontrada), no de una fila. */
  fila: number;
  /** Código ESTABLE del motivo (p.ej. `usuario-no-encontrado`, `fundamental-invalido`). */
  codigo: string;
  motivo: string;
}

/** Resultado de POST v1/winners/import (CSV histórico 3 años). Espejo de WinnerImportResultDto. */
export interface WinnerImportResultDto {
  totalFilas: number;
  importados: number;
  rechazados: number;
  errores: WinnerImportErrorDto[];
  /** `true` si esta corrida fue solo de validación (`preview: true` en la petición). */
  preview: boolean;
  /** CSV descargable (`fila,codigo,mensaje`) con el detalle de `errores`. Vacío si no hubo errores. */
  errorsCsv: string;
}

/**
 * Resultado de POST v1/users/bulk-import (y europe-import) — carga masiva del padrón Frontline.
 * Espejo de PMP.UserAdminService BulkImportFrontlineResult. Idempotente por correo; el backend
 * reporta a nivel lote (errors: string[]), no por fila. RF-010/RF-114.
 */
export interface BulkImportUsersResultDto {
  received: number;
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
  reportingLabel?: string | null;
}

// ─── WP-15 REQ-132 — Campañas (wizard 5 pasos, CampaignsController) ───────────────

/** Solicitud de aprobación de visibilidad de una campaña. Espejo de CampaignApprovalDto. */
export interface CampaignApprovalDto {
  id: string;
  campaignId: string;
  /** "pendiente" | "aprobada" | "rechazada". */
  estado: string;
  solicitadoPor: string;
  solicitadoAt: string;
  resueltoPor?: string | null;
  resueltoAt?: string | null;
  comentario?: string | null;
}

/** Acción del flujo de aprobación (POST v1/campaigns/{id}/approval). */
// `cancel` (WP-B5/RN-C11) lo usa el SOLICITANTE para retirar su propia solicitud pendiente; a
// diferencia de los otros tres no devuelve DTO —la fila deja de existir— y responde 204.
export type CampaignApprovalAction = 'submit' | 'approve' | 'reject' | 'cancel';

/** Cuerpo de POST v1/campaigns — crea el borrador (wizard). Espejo de CreateCampaignBody. */
export interface CreateCampaignBody {
  nombre: LocalizedText;
  tipo: CampaignType;
  zonaHoraria: string;
  alcance: string[];
  fechaInicio?: string | null;
  fechaFin?: string | null;
  descripcion?: LocalizedText | null;
  exclusiones?: string[] | null;
  visibilidad?: CampaignVisibilityDto | null;
  parentCampaignId?: string | null;
  landingSlug?: string | null;
  diseno?: LocalizedText | null;
  /** Banner propio de la campaña (subtítulo por idioma). Omitido/null = hereda el banner global/de programa. */
  bannerSubtitle?: Record<string, string> | null;
  /** Logo del banner por idioma, propio de la campaña. */
  bannerLogoUrl?: Record<string, string> | null;
  /** RF-037 — Mínimo de racha propio de la campaña (1–20). Omitido/null = usa el global de Gestión. */
  streakWeeklyMinimum?: number | null;
  /** Tope de envíos propio de la campaña (1–1000). Omitido/null = usa el global de Gestión. */
  maxRecognitionsPerPeriod?: number | null;
  /** Bug UAT #18 — «No aplica»: crea la campaña sin tope de envíos (ignora el global de Gestión). */
  recognitionsLimitDisabled?: boolean;
  /** Bug UAT #18 — número propio de «veces mismo fundamental por persona». Omitido/null = usa el global. */
  maxSameFundamentalPerPerson?: number | null;
  /** Bug UAT #18 — «No aplica»: sin límite de mismo-fundamental-por-persona (piso GDPR aparte). */
  sameFundamentalLimitDisabled?: boolean;
}

/** Cuerpo de PATCH v1/campaigns/{id} — actualiza el borrador. Espejo de UpdateCampaignBody. */
export interface UpdateCampaignBody {
  nombre?: LocalizedText | null;
  descripcion?: LocalizedText | null;
  fechaInicio?: string | null;
  fechaFin?: string | null;
  zonaHoraria?: string | null;
  alcance?: string[] | null;
  exclusiones?: string[] | null;
  visibilidad?: CampaignVisibilityDto | null;
  diseno?: LocalizedText | null;
  /** Banner propio de la campaña (subtítulo por idioma). Omitido/null = NO se toca (mismo criterio que `diseno`). */
  bannerSubtitle?: Record<string, string> | null;
  /** Logo del banner por idioma, propio de la campaña. */
  bannerLogoUrl?: Record<string, string> | null;
  /**
   * RF-037 — Nuevo mínimo propio de la campaña (1–20). Omitido/null = NO se toca: en un PATCH `null`
   * significa "sin cambio" para todos los campos de este cuerpo. Para quitarlo hay que pedirlo
   * explícitamente con `clearStreakWeeklyMinimum`.
   */
  streakWeeklyMinimum?: number | null;
  /** RF-037 — true devuelve la campaña al mínimo global de Gestión. */
  clearStreakWeeklyMinimum?: boolean;
  /**
   * Nuevo tope propio de la campaña (1–1000). Omitido/null = NO se toca (mismo criterio que
   * streakWeeklyMinimum: en un PATCH, null significa "sin cambio").
   */
  maxRecognitionsPerPeriod?: number | null;
  /** true devuelve la campaña al tope global de Gestión. */
  clearMaxRecognitionsPerPeriod?: boolean;
  /**
   * Bug UAT #18 — «No aplica» el tope de envíos. Omitido/undefined = no se toca; true = sin tope
   * (ignora el global); false = reactiva el tope (vuelve a número propio / herencia).
   */
  recognitionsLimitDisabled?: boolean;
  /** Bug UAT #18 — nuevo número «veces mismo fundamental por persona». Omitido/null = no se toca. */
  maxSameFundamentalPerPerson?: number | null;
  /** Bug UAT #18 — true devuelve la campaña al global de mismo-fundamental. */
  clearMaxSameFundamentalPerPerson?: boolean;
  /** Bug UAT #18 — «No aplica» del mismo-fundamental. Omitido = no se toca; true = sin límite; false = reactiva. */
  sameFundamentalLimitDisabled?: boolean;
  /** Grilla "Horarios y Fechas por País" (Brillamos). Omitido/undefined = no se toca; lista = reemplaza. */
  horariosPorPais?: CampaignCountryScheduleDto[];
  /**
   * RF-130 — Nueva fecha de release del Wrapped. Omitido/null = NO se toca (mismo criterio que el
   * resto de este PATCH), así que retirar una fecha ya fijada necesita la bandera de abajo.
   * Día de calendario interpretado en la `zonaHoraria` de la campaña.
   */
  wrappedReleaseAt?: string | null;

  /**
   * C2/P17 — nueva frecuencia declarativa. `undefined` = no se toca; cadena VACÍA = se limpia.
   * Sin esa distinción, un guardado del wizard que no la mandara la borraría cada vez.
   */
  frecuencia?: string | null;

  /** C2/P19 — nueva meta de participantes. `undefined` = no se toca. */
  metaParticipantes?: number | null;

  /** C2/P19 — true retira la meta: la columna «% de meta» vuelve a «—». */
  clearMetaParticipantes?: boolean;
  /** RF-130 — true retira la fecha: el Wrapped vuelve a esperar el cierre de la campaña. */
  clearWrappedReleaseAt?: boolean;
}

/** Cuerpo de POST v1/campaigns/{id}/documents. Espejo de AddDocumentBody. */
/** FASE 3 — alta MULTIPART de un documento de campaña (subir PDF real, ya no pegar URL). */
export interface UploadCampaignDocumentInput {
  file: File;
  titulo: LocalizedText;
  languageCode: string;
  categoria?: string;
  orden?: number | null;
}

// ─── CPE-138 — Campañas por Localidad (excepciones, CampaignExceptionsController) ─
//
// Overrides puntuales de la config de un programa (Fundamentales/Brillamos) para un alcance OU/BU/Site.
// Un Admin OU las PROPONE → nacen pendientes + inactivas; el Admin Global las aprueba/rechaza. Endpoints
// top-level v1/campaign-exceptions. Espejo de PMP.SharedContracts.DTOs.Recognition.CampaignExceptionDto.

/** Tipo/concepto de una excepción — espejo de CampaignExceptionType (serializado por nombre). */
export type CampaignExceptionType =
  | 'TopeSemanal'
  | 'DesactivarEnvio'
  | 'HorarioRestringido'
  | 'Visibilidad'
  | 'Banner';

/**
 * Valores del override de una excepción. TODOS los campos son opcionales: null = "no sobreescribe,
 * hereda la config base". Espejo de CampaignExceptionOverrideDto.
 */
export interface CampaignExceptionOverrideDto {
  /** TopeSemanal — máximo de reconocimientos por período. */
  topeMaximo?: number | null;
  /** "Semanal" | "Diario" | "Mensual". */
  topeFrecuencia?: string | null;
  /** DesactivarEnvio — apaga el envío para el alcance. */
  desactivarEnvio?: boolean | null;
  /** HorarioRestringido — ventana horaria de envío ("09:00"). */
  horaInicio?: string | null;
  horaFin?: string | null;
  /** Zona horaria IANA de la ventana. */
  zonaHoraria?: string | null;
  fechaInicio?: string | null;
  fechaFin?: string | null;
  /** Visibilidad — override de la matriz (reusa el DTO existente). */
  visibilidad?: CampaignVisibilityDto | null;
  /** Racha — mínimo semanal propio de la excepción. */
  rachaMinimoSemanal?: number | null;

  // ── Reglas de envío por localidad (RN-E03/RN-PI03) — Tarea 9 SDD 2026-08-17 ──────────
  // El backend ya las aplicaba (LocalityExceptionEnforcement/ConfigRecognitionPolicyProvider); el
  // frontend no las declaraba. Mismo criterio que el resto del DTO: null = "no sobreescribe, hereda
  // la config base".
  /** RN-E03 — permite marcar más de un Fundamental por reconocimiento. */
  multiFundamental?: boolean | null;
  /** Máximo de Fundamentales por envío cuando `multiFundamental` está activo. */
  maxFundamentales?: number | null;
  /** «Multi-persona» — permite varios receptores en un mismo reconocimiento. */
  multipersona?: boolean | null;
  /** «Comentario obligatorio» al enviar. */
  comentarioObligatorio?: boolean | null;
  /** Mínimo de caracteres cuando `comentarioObligatorio` está activo. */
  minCaracteres?: number | null;
  /** «Botón Editar» / «Botón Eliminar» (solo enviados) — viajan juntos, como el global. */
  editarYEliminar?: boolean | null;
  /** «Carga CSV» — habilita la importación masiva para el alcance (por emisor, fila a fila). */
  cargaCsv?: boolean | null;
  /** RN-PI03 — qué puertas de envío están abiertas: "app" | "postit" | "ambos". */
  entryPointMode?: string | null;

  // ── Banner "Brillamos Juntos" por localidad — Tarea 14 SDD 2026-08-17 ────────────────────
  // El banner del hero de Brillamos es 100% global (solo Admin Global, GestionSettingsDto);
  // estos 2 campos permiten que una excepción por localidad lo SOBRESCRIBA para su alcance.
  // Mismo shape que GestionSettingsDto.brillamosBannerSubtitle/brillamosBannerLogoUrl (clave =
  // código de idioma); null = "no sobreescribe, hereda el banner global".
  /** Subtítulo del banner por idioma, propio de esta excepción. */
  bannerSubtitle?: Record<string, string> | null;
  /** Logo del banner por idioma (data-URI o URL), propio de esta excepción. */
  bannerLogoUrl?: Record<string, string> | null;

  // ── Ranking y Moderación por localidad (paso "Reglas") ─────────────────────────────────
  /** «Mostrar Ranking» — visibilidad del leaderboard de OUs (eje del viewer). Se compone con AND
   *  sobre el toggle global "leaderboard" de Admin → Vistas de Usuario, nunca lo sustituye. */
  mostrarRanking?: boolean | null;
  /** «Permitir reportar» — eje del contenido: restringe (nunca amplía) la moderación por geografía
   *  ya existente (REQ-053/RF-084). */
  permiteReportar?: boolean | null;
}

/**
 * Excepción / Campaña por Localidad. Nace PENDIENTE e INACTIVA hasta aprobación del Admin Global.
 * Espejo de CampaignExceptionDto.
 */
export interface CampaignExceptionDto {
  id: string;
  tenantId: string;
  tipo: CampaignExceptionType;
  programa: CampaignType;
  /** Campaña base; null = override sobre Fundamentales (no es una fila de campaña). */
  baseCampaignId: string | null;
  /** Ids OU/BU/Site del alcance (mismo shape que CampaignDto.alcance). */
  alcance: string[];
  /** Snapshot legible del alcance para la tabla: "México › Carnes Frías › 2 sites". */
  alcanceEtiqueta: string | null;
  override: CampaignExceptionOverrideDto;
  /** "Estado Campaña" de la tabla: Activa/Inactiva. */
  activa: boolean;
  /** "pendiente" | "aprobada" | "rechazada". */
  estadoAprobacion: string;
  solicitadoPor: string;
  solicitadoAt: string;
  resueltoPor?: string | null;
  resueltoAt?: string | null;
  /** Motivo de rechazo (obligatorio al rechazar). */
  comentario?: string | null;
  descripcion?: LocalizedText | null;
  updatedAt?: string | null;
}

/** Cuerpo de POST v1/campaign-exceptions — propone una excepción. Espejo de CreateExceptionBody. */
export interface CreateCampaignExceptionBody {
  tipo: CampaignExceptionType;
  programa: CampaignType;
  alcance: string[];
  override: CampaignExceptionOverrideDto;
  baseCampaignId?: string | null;
  alcanceEtiqueta?: string | null;
  descripcion?: LocalizedText | null;
}

/** Cuerpo de PUT v1/campaign-exceptions/{id} — edita una excepción. Espejo de UpdateExceptionBody. */
export interface UpdateCampaignExceptionBody {
  tipo?: CampaignExceptionType | null;
  programa?: CampaignType | null;
  baseCampaignId?: string | null;
  alcance?: string[] | null;
  alcanceEtiqueta?: string | null;
  override?: CampaignExceptionOverrideDto | null;
  descripcion?: LocalizedText | null;
}

/** Cuerpo de POST v1/campaign-exceptions/{id}/toggle — enciende/apaga. Espejo de ToggleBody. */
export interface ToggleCampaignExceptionBody {
  activa: boolean;
}

/** Acción del flujo de aprobación de una excepción (POST .../{id}/approval). */
export type CampaignExceptionApprovalAction = 'submit' | 'approve' | 'reject';

/** Cuerpo de POST v1/campaign-exceptions/{id}/approval. Espejo de ApprovalBody. */
export interface CampaignExceptionApprovalBody {
  action: CampaignExceptionApprovalAction;
  comentario?: string | null;
}

/** Filtros del listado GET v1/campaign-exceptions (paginado). */
export interface CampaignExceptionListParams {
  programa?: CampaignType;
  baseCampaignId?: string;
  activa?: boolean;
  /** "pendiente" | "aprobada" | "rechazada". */
  estado?: string;
  page?: number;
  pageSize?: number;
}

// ─── Tarea 15 (SDD 2026-08-17) — Documentos de excepción, persistencia real ─────
//
// El paso "Documentos" del wizard de excepción era un mockup (`ExcDoc[]` en estado local de
// `CampaignExceptions.tsx`) que nunca llamaba al backend. Sigue el MISMO patrón ya establecido para
// documentos de campaña: se suben/borran con llamadas SEPARADAS (no viajan embebidos en
// `CreateCampaignExceptionBody`), una vez que la excepción ya tiene un `id` (borrador guardado).
//
// El backend (task-15-backend-brief.md, contrato acordado en paralelo) reutiliza EL MISMO DTO que ya
// usan los documentos de campaña — `GET v1/campaign-exceptions/{id}/documents` devuelve
// `CampaignDocumentDto`, no un tipo propio — así que aquí es un alias directo, no una interfaz nueva.

/** Documento adjunto a una excepción de campaña ("Campañas por Localidad"). Mismo shape que
 *  `CampaignDocumentDto` (el backend reutiliza el DTO de documentos de campaña sin cambios). */
export type CampaignExceptionDocumentDto = CampaignDocumentDto;

/** Alta MULTIPART de un documento de excepción — mismo shape que `UploadCampaignDocumentInput`. */
export type UploadCampaignExceptionDocumentInput = UploadCampaignDocumentInput;

// ─── WP-15 REQ-134 — Gestión (AdminSettingsController) ───────────────────────────

/**
 * Insignia en la pantalla de Gestión (GestionSettingsDto.badges y v1/admin/settings/badges). Mismo
 * contrato que `BadgeDto` —el backend serializa un solo record para las dos superficies— pero con
 * `nombre` e `isActive` obligatorios, porque el formulario de Gestión siempre los envía: son campos
 * de edición, no datos opcionales de lectura. (Hasta S1 este tipo divergía del `BadgeDto` de
 * lectura, que declaraba `names`/`icon`, campos que el backend nunca emitió.)
 */
export interface AdminBadgeDto {
  id: string;
  code: string;
  nombre: LocalizedText;
  descripcion?: LocalizedText | null;
  /** Semanas consecutivas requeridas (insignias de racha). */
  semanasRequeridas?: number | null;
  /** Icono PNG (no emoji). */
  iconUrl?: string | null;
  /** Color del brandbook. null = "No aplica". */
  color?: string | null;
  isActive: boolean;
  /**
   * `BadgeCategoria` del backend. Opcional para no romper los consumidores de Gestión (Streak),
   * que nunca la envían — el backend la asume `Streak` por defecto. `WinnersController` la fuerza
   * a `Campaign` server-side sin importar lo que mande el cliente.
   */
  categoria?: 'Streak' | 'Campaign';
  /** Subtítulo de año de la insignia de campaña (p. ej. "2026"), ×7 idiomas. Solo aplica a Campaign. */
  anioSubtitulo?: LocalizedText | null;
  /**
   * Fila 40 — año del premio COMO NÚMERO, que es el que se valida al otorgar la insignia.
   * `anioSubtitulo` es su rótulo y puede no ser un año ("Innovación LATAM"), así que no sirve para
   * decidir. `null`/ausente = la insignia no declara año y no se valida nada contra ella.
   */
  anio?: number | null;
}

/**
 * RF-037 — Periodicidad de la racha. Solo Semanal y Mensual: 'biweekly' salió del contrato porque
 * ningún cálculo lo consumía nunca y la spec no lo contempla.
 */
export type StreakPeriodicity = 'weekly' | 'monthly';

/** Las dos opciones, en el orden en que las ofrece la pantalla de Gestión. */
export const STREAK_PERIODICITIES: readonly StreakPeriodicity[] = ['weekly', 'monthly'];

/**
 * T1 / RF-107 — Ventana de edición post-envío. Las cuatro opciones cerradas del `select` de la
 * maqueta (`fundamentales-admin.js:1163`), en su mismo orden.
 */
export type PostEditWindowCode = '24h' | '48h' | '7d' | 'unlimited';

export const POST_EDIT_WINDOWS: readonly PostEditWindowCode[] = ['24h', '48h', '7d', 'unlimited'];

/**
 * Lo que la pantalla debe mostrar marcado para un valor dado.
 *
 * 🔴 Distingue AUSENCIA de BASURA, exactamente igual que el backend (`PostEditWindowPolicy.cs` en
 * SurveyService — es la misma regla escrita dos veces porque son dos lenguajes; si cambia una, cambia
 * la otra, o la pantalla dirá algo distinto de lo que el servidor aplica).
 *
 * · Sin valor ⇒ `'unlimited'`. Nunca se configuró y hoy `allowPostEdit` no caduca. Marcar
 *   «24 horas» aquí sería peor que cosmético: el siguiente Guardar persistiría un `'24h'` que el
 *   administrador nunca eligió y le acortaría la ventana sin enterarse.
 * · Valor ilegible ⇒ `'24h'`, que es a lo que cae el servidor. No se concede «para siempre» por un
 *   valor que nadie escribió a propósito.
 */
export function toPostEditWindow(value: string | null | undefined): PostEditWindowCode {
  const raw = (value ?? '').trim().toLowerCase();
  if (raw === '') return 'unlimited';
  return (POST_EDIT_WINDOWS as readonly string[]).includes(raw) ? (raw as PostEditWindowCode) : '24h';
}

/**
 * RF-037 — Rango del mínimo de racha, en UN solo sitio del frontend. Lo exigen la pantalla de Gestión
 * (mínimo global) y el asistente de campañas (mínimo propio); con el literal duplicado, cambiar el
 * rango dejaría una de las dos atrás. Espejo de `StreakCalculator.MinimumFloor/MinimumCeiling`.
 */
export const STREAK_MINIMUM_FLOOR = 1;
export const STREAK_MINIMUM_CEILING = 20;

/** Acota un mínimo de racha al rango válido, redondeando lo que no sea entero. */
export function clampStreakMinimum(value: number): number {
  return Math.max(STREAK_MINIMUM_FLOOR, Math.min(STREAK_MINIMUM_CEILING, Math.round(value)));
}

/**
 * Rango del tope de envíos propio de una campaña, en UN solo sitio del frontend — mismo techo que
 * ya usa la pantalla de Gestión para el global (`SaveGestionSettingsCommandHandler.Math.Clamp(...,
 * 1, 1000)` en el backend).
 */
export const RECOGNITIONS_LIMIT_FLOOR = 1;
export const RECOGNITIONS_LIMIT_CEILING = 1000;

/** Acota un tope de envíos al rango válido, redondeando lo que no sea entero. */
export function clampRecognitionsLimit(value: number): number {
  return Math.max(RECOGNITIONS_LIMIT_FLOOR, Math.min(RECOGNITIONS_LIMIT_CEILING, Math.round(value)));
}

/**
 * Periodicidad utilizable a partir de lo que devuelva el backend. Un documento guardado antes de
 * RF-037 puede traer 'biweekly': sin este saneo el `<select>` no casaría con ninguna opción y el
 * administrador guardaría de vuelta un valor que el backend ya rechaza.
 */
export function toStreakPeriodicity(value: string | null | undefined): StreakPeriodicity {
  return value?.trim().toLowerCase() === 'monthly' ? 'monthly' : 'weekly';
}

/**
 * Documento de Gestión (config global del módulo, REQ-049). PUT reemplaza el documento de forma
 * atómica (nada persiste sin Guardar). Espejo de GestionSettingsDto.
 */
export interface GestionSettingsDto {
  /** Descripción de la racha por idioma (las 7 llaves deben venir completas). */
  streakDescription: LocalizedText;
  /** Insignias (nombres ×7, icono PNG, color nullable). */
  badges: AdminBadgeDto[];
  streakEnabled: boolean;
  /** Mínimo semanal de envíos para conservar la racha (1–20). */
  weeklyMinimum: number;
  /** Ventana de freeze global de racha (rango de fechas, NO por OU). */
  freezeFrom?: string | null;
  freezeTo?: string | null;
  multinomination: boolean;
  dailyHabitsMandatory: boolean;
  /**
   * RF-037 — Período sobre el que se exige `weeklyMinimum`. La spec contempla SOLO Semanal y Mensual;
   * el backend rechaza cualquier otro valor al guardar y normaliza a 'weekly' lo ya persistido.
   */
  periodicity: StreakPeriodicity;
  antiSpoiler: boolean;
  /**
   * WP-E4 / RF-168 — Ventana del Modo Anti-Spoiler (ISO-8601 UTC). GLOBAL, no por OU: la maqueta
   * (`fundamentales-admin.js:1163`) tiene toggle + Desde + Hasta y nada de OU.
   *
   * El toggle NO se sustituye, se acompaña: `antiSpoiler` sigue siendo el interruptor maestro y
   * estas dos fechas solo lo acotan. Los dos en `null` ⇒ comportamiento de siempre, por eso el
   * cambio no exige migrar ningún documento. Se admite MEDIA ventana (solo una de las dos).
   */
  antiSpoilerFrom?: string | null;
  antiSpoilerTo?: string | null;
  wrappedByOu: boolean;
  /**
   * WP-D1 (Task 0.1) — apaga/enciende el módulo de Ganadores desde Gestión (`GET/PUT
   * v1/admin/settings`). Opcional y no `undefined` ⇒ `true`: el backend (`GamificationRepository`)
   * ya trata así el campo ausente en documentos legados, así que el frontend replica el mismo
   * default en vez de asumir un valor que rompería fixtures existentes que aún no lo declaran.
   */
  winnersEnabled?: boolean;
  /** WP-07.2 — Diseño del banner de Fundamentales por idioma (opcional, no exige los 7). */
  bannerTitleLine1?: LocalizedText | null;
  bannerTitleLine2?: LocalizedText | null;
  bannerSubtitle?: LocalizedText | null;
  /** URL del logo PNG por idioma (el texto va en la imagen). Subida a blob = rebanada 2. */
  bannerLogoUrl?: LocalizedText | null;
  /** Banner PROPIO de Brillamos (subtítulo + logo por idioma) para el hero de /app/brillamos — lo edita
   * la pestaña Brillamos y lo sirve el GET no-admin del landing (cada programa su banner). */
  brillamosBannerSubtitle?: LocalizedText | null;
  brillamosBannerLogoUrl?: LocalizedText | null;
  /** Modalidad de reconocimiento: 'A' (clásico) | 'B' (post-it) | 'both' (A/B test). */
  entryPointMode?: 'A' | 'B' | 'both';
  /** RN-PI03 — entry point PROPIO de Brillamos (paralelo a `entryPointMode`, que es el de Fundamentales).
   *  Default 'A' (clásico) = comportamiento actual de Brillamos. */
  brillamosEntryPointMode?: 'A' | 'B' | 'both';
  /**
   * WP-F5 — Ids de Fundamentales que el programa NO ofrece («Fundamentales disponibles»).
   * 🔴 Es lista de EXCLUIDOS, no de permitidos: con una de permitidos, cualquier Fundamental añadido
   * después al catálogo nacería invisible y el documento guardado no lo nombraría.
   * ⚠️ NO es `FundamentalDto.isActive`, que es el borrado lógico del catálogo de Pulso (D-27).
   */
  fundamentalesDeshabilitados?: string[] | null;
  /** WP-F5 — el frontline que entra por webapp puede personalizar su avatar de color. */
  avatarFrontlineEnabled?: boolean;
  /** WP-F5 — muestra el selector de avatar justo después del registro frontline. */
  avatarSelectorPostRegistro?: boolean;
  /**
   * WP-F5 — texto de ayuda del campo de mensaje al reconocer, por idioma.
   * 🔴 Vacío ⇒ se usa el i18n del producto, NO una caja en blanco: un placeholder vacío quita la
   * única pista de qué escribir. Por eso no se exigen los 7 idiomas.
   */
  messagePlaceholder?: LocalizedText | null;
  /** Comentario obligatorio al reconocer. */
  commentRequired?: boolean;
  // ── Config unificada de Campañas → Fundamentales (maqueta 1:1) ──────────────
  // Reglas de envío
  multiFundamental?: boolean;
  minCommentChars?: number;
  bulkCsvEnabled?: boolean;
  reactionsEnabled?: boolean;
  sendingDisabled?: boolean;
  /** Permite el envío anónimo en TODO (Fundamentales/Brillamos/Personalizadas) salvo que la campaña
   *  defina su propia configuración. No afecta la anonimización automática por GDPR en Europa. */
  permitirAnonimos?: boolean;
  /** Permite promover/enviar de Fundamentales hacia Brillamos (check en Reconocer + ⭐ del muro/perfil). */
  allowPromoteToBrillamos?: boolean;
  // Moderación (Reportar/Filtro de palabras viven en ModerationSettings)
  allowPostEdit?: boolean;
  /**
   * T1 / RF-107 — Plazo para editar un reconocimiento después de enviarlo.
   *
   * 🔴 `null`/ausente significa SIN LÍMITE, no «24 h», aunque la maqueta traiga «24 horas»
   * preseleccionado: hoy `allowPostEdit` no caduca, y hacer que la ausencia valga 24 h acortaría en
   * silencio la edición de toda OU que lo tenga encendido. La maqueta manda para la ESTRUCTURA (un
   * `select` con esas cuatro opciones); cuál sale marcada depende del dato.
   */
  postEditWindow?: PostEditWindowCode | null;
  // Racha
  streakVisibleInProfile?: boolean;
  badgesEnabled?: boolean;
  /** Anti-abuso de racha (REQ-043): tope de envíos 1:1 a la misma persona que cuentan para la racha. */
  streakPerPersonCapEnabled?: boolean;
  /** Cuántos envíos 1:1 a la misma persona cuentan para la racha por período (1–50). */
  streakPerPersonCap?: number;
  // Topes (gestión de límites)
  limitsEnabled?: boolean;
  maxRecognitionsPerPeriod?: number;
  /** 'weekly' | 'monthly' | 'daily' */
  recognitionsPeriod?: string;
  recognitionsLimitActive?: boolean;
  maxFundamentalsPerRecognition?: number;
  fundamentalsLimitActive?: boolean;
  /** Nombre/Descripción editables de los 2 topes (null/omitido = el front usa su texto por defecto i18n). */
  recognitionsLimitName?: string | null;
  recognitionsLimitDescription?: string | null;
  fundamentalsLimitName?: string | null;
  fundamentalsLimitDescription?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

/**
 * Proyección de GestionSettingsDto segura para cualquier empleado (GET v1/view-settings/recognition-rules).
 * Espejo de RecognitionRulesDto.
 */
export interface RecognitionRulesDto {
  multinomination: boolean;
  dailyHabitsMandatory: boolean;
  antiSpoiler: boolean;
  multiFundamental: boolean;
  commentRequired: boolean;
  minCommentChars: number;
  sendingDisabled: boolean;
  bulkCsvEnabled: boolean;
  allowPostEdit: boolean;
  entryPointMode: string;
  streakVisibleInProfile: boolean;
  badgesEnabled: boolean;
  /** T5 — permite ofrecer el check "enviar también a Brillamos" en Reconocer. */
  allowPromoteToBrillamos?: boolean;
  /**
   * T1 / RF-107 — Ventana de edición EN HORAS, ya resuelta por el backend. `null`/ausente ⇒ sin
   * límite de tiempo (que no es lo mismo que sin edición: eso lo dice `allowPostEdit`).
   *
   * A diferencia de `antiSpoilerActivoAhora`, esto NO puede viajar resuelto a un sí/no: la respuesta
   * depende de la fecha de CADA reconocimiento. Quien decide sigue siendo el servidor —el PATCH
   * responde 403 fuera de plazo—; esto solo sirve para no ofrecer un lápiz que va a fallar.
   */
  postEditWindowHours?: number | null;
  /**
   * WP-E4 / RF-168 — el anti-spoiler EFECTIVO, ya resuelto por el backend contra la ventana.
   *
   * 🔴 Este es el que hay que mirar para decidir qué se enseña, NO `antiSpoiler`. `antiSpoiler` es
   * solo el estado del interruptor; este es el interruptor Y la ventana. Y viaja resuelto a
   * propósito: si el navegador comparase `From ≤ ahora ≤ To` por su cuenta, la regla viviría en dos
   * relojes y el del navegador se puede cambiar. El backend ya lo aplica para NEGAR el acceso
   * (403 en `GET v1/recognitions?receptorId=…`); esto es lo mismo que él decidió, para no ofrecer
   * un botón que el servidor va a rechazar.
   *
   * Opcional en el tipo porque un backend anterior a WP-E4 no lo manda; el defecto es `false`.
   */
  antiSpoilerActivoAhora?: boolean;
  /**
   * Tope EFECTIVO de fundamentales por envío, ya resuelto por el backend (misma lógica que
   * `ConfigRecognitionPolicyProvider`) SIN el piso GDPR — ese lo aplica el cliente con `isGdprRestricted`.
   * `int.MaxValue` (2147483647) = sin tope (se acota solo por los fundamentales disponibles en el catálogo).
   * Opcional: un backend viejo no lo manda y el picker cae al default histórico de 2.
   */
  maxFundamentalsPerRecognition?: number;
  /** RN-PI03 — entry point PROPIO de Brillamos (post-it A/B/both) para el landing de Brillamos, resuelto
   *  a nivel programa. Default 'A' (clásico) = comportamiento actual. */
  brillamosEntryPointMode?: string;
  /**
   * Si quien pregunta puede enviar un reconocimiento anónimo AHORA MISMO (campaña > Gestión).
   * Opcional: un backend anterior a este campo no lo manda, y el defecto de los consumidores es
   * `true` (el anonimato siempre estuvo permitido porque nada lo validaba).
   */
  anonymousAllowed?: boolean;
  /**
   * WP-F5 — Ids de Fundamentales que el programa ha retirado. El picker de Reconocer no los ofrece.
   * 🔴 Llega por AQUÍ y no por el documento de Gestión porque aquel es AdminGlobal-only y su
   * consumidor natural es un colaborador, que recibiría 403 — la lección de `A5`.
   * ⚠️ No sustituye al gate: el servidor rechaza igual un Fundamental retirado.
   */
  fundamentalesDeshabilitados?: string[] | null;
  /**
   * WP-F5 — Texto de ayuda configurable del campo de mensaje, POR IDIOMA (sin resolver, como
   * `LandingBannerDto`). Vacío o sin entrada para el idioma activo ⇒ se usa el i18n del producto:
   * una caja en blanco quita la única pista de qué escribir.
   */
  messagePlaceholder?: LocalizedText | null;
  /**
   * R7 — Tope EFECTIVO de envíos por período (Gestión ∧ excepción de localidad activa), el MENOR de
   * los dos, ya resuelto por el backend con el mismo criterio que `ConfigRecognitionPolicyProvider`.
   * No sustituye al gate real (el conteo real del emisor no viaja en este DTO); es un aviso, no una
   * validación. `null`/ausente = sin ningún tope vigente que anunciar.
   */
  weeklySendLimitEffective?: number | null;
  /**
   * R7 — `true` si, AHORA MISMO, el emisor está fuera de la ventana de horario restringido de una
   * excepción de localidad activa (`HorarioRestringido`). Mismo espíritu que
   * `antiSpoilerActivoAhora`: viaja ya resuelto contra el reloj del servidor, para no ofrecer un
   * botón que `BrillamosScheduleGate`/`CreateRecognitionCommandHandler` va a rechazar. Opcional: un
   * backend anterior a R7 no lo manda.
   */
  scheduleRestricted?: boolean;
  /**
   * «Mostrar Ranking» — visibilidad EFECTIVA del leaderboard de OUs en la pantalla de Brillamos, ya
   * compuesta con la excepción de localidad de la OU del emisor (eje del viewer). Se compone con AND
   * sobre el toggle global "leaderboard" de Admin → Vistas de Usuario (`useVistaHabilitada`), nunca
   * lo sustituye. Opcional: un backend anterior a este campo no lo manda; el default es `true`.
   */
  rankingVisible?: boolean;
}

/**
 * Config de reglas de envío POR PROGRAMA (Fundamentales / Brillamos / Personalizada). Espejo de
 * `RecognitionProgramSettingsDto` del backend. Doc por (tenant × programa); TODOS los campos son
 * anulables: `null`/ausente = "hereda el valor global de Gestión". Se edita en la pestaña de cada
 * programa (Campañas → Brillamos/Custom) y se resuelve `instancia ?? programa ?? global`.
 */
export interface RecognitionProgramSettingsDto {
  /** "Fundamentales" | "Brillamos" | "Personalizada" (CampaignType). Clave del doc. */
  programa: string;
  entryPointMode?: string | null;
  multinomination?: boolean | null;
  multiFundamental?: boolean | null;
  maxFundamentalsPerRecognition?: number | null;
  fundamentalsLimitActive?: boolean | null;
  commentRequired?: boolean | null;
  minCommentChars?: number | null;
  bulkCsvEnabled?: boolean | null;
  reactionsEnabled?: boolean | null;
  sendingDisabled?: boolean | null;
  dailyHabitsMandatory?: boolean | null;
  /** Permite envíos anónimos. null = hereda el global de Gestión. */
  permitirAnonimos?: boolean | null;
  limitsEnabled?: boolean | null;
  maxRecognitionsPerPeriod?: number | null;
  recognitionsPeriod?: string | null;
  recognitionsLimitActive?: boolean | null;
  /** Moderación — edición post-envío. null = hereda el global de Gestión (ventana y piso GDPR siguen globales). */
  allowPostEdit?: boolean | null;
  /** Moderación — interruptor del anti-spoiler. null = hereda global. La ventana (from/to) sigue global. */
  antiSpoiler?: boolean | null;
  // Racha por-programa (Rama B) — cada programa lleva su racha; null = hereda el global de Gestión.
  streakEnabled?: boolean | null;
  periodicity?: string | null;
  weeklyMinimum?: number | null;
  streakVisibleInProfile?: boolean | null;
  badgesEnabled?: boolean | null;
  freezeFrom?: string | null;
  freezeTo?: string | null;
  streakPerPersonCapEnabled?: boolean | null;
  streakPerPersonCap?: number | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

/**
 * WP-07.3 — Subconjunto del banner de Fundamentales que el LANDING (/app/reconocer, /app/brillamos)
 * lee SIN rol admin (GET v1/view-settings/banner). Cada campo es texto por idioma (opcional); la vista
 * cae a los literales i18n / SVG estáticos cuando viene null o vacío.
 */
export interface LandingBannerDto {
  bannerTitleLine1?: LocalizedText | null;
  bannerTitleLine2?: LocalizedText | null;
  bannerSubtitle?: LocalizedText | null;
  bannerLogoUrl?: LocalizedText | null;
  /** Banner de Brillamos (subtítulo + logo por idioma) para el hero de /app/brillamos. */
  brillamosBannerSubtitle?: LocalizedText | null;
  brillamosBannerLogoUrl?: LocalizedText | null;
}

/** Cuerpo de PUT v1/admin/settings/streak-freeze. Espejo de StreakSettingsBody. */
export interface StreakSettingsBody {
  weeklyMinimum: number;
  enabled: boolean;
  freezeFrom?: string | null;
  freezeTo?: string | null;
}

// ─── Recap anual (Wrapped) — administración por edición ───────────────────────────
// Espejo de RecapDtos.cs (SharedContracts). El admin configura la capa visual y los textos:
// los DATOS del usuario (recibidos/enviados, Fundamental top, racha) los inyecta AnalyticsService.

/** Un slide de la edición. `clave` enlaza la config visual con el dato que inyecta la plataforma. */
export interface RecapSlideDto {
  indice: number;
  /** portada | numeros | define | mensaje | quienes | red | constancia | reto | ganador */
  clave: string;
  /** Título por idioma (7 llaves completas si el slide está activo). */
  titulo: LocalizedText;
  /** Color o gradiente CSS del slide. */
  fondo: string;
  /** Emoji o URL. null ⇒ automático (el slide de ganador usa la insignia real). */
  icono?: string | null;
  activo: boolean;
  /** Textos bajo cada número del slide (vacío si el slide no tiene números). */
  etiquetasNumero?: string[] | null;
}

/** Mensaje de respaldo: usuario sin reconocimientos en el periodo o edición aún no visible. */
export interface RecapGenericMessageDto {
  titulo: LocalizedText;
  descripcion: LocalizedText;
  imagenUrl?: string | null;
}

/** Edición del Recap. El PUT reemplaza el documento de forma atómica (nada persiste sin Guardar). */
export interface RecapEditionDto {
  anio: number;
  nombre: string;
  /** OUs alcanzadas. Vacío ⇒ global (todas). */
  alcance: string[];
  slides: RecapSlideDto[];
  mensajeGenerico: RecapGenericMessageDto;
  activo: boolean;
  visibleDesde?: string | null;
  visibleHasta?: string | null;
  /** Fecha real de la última difusión. null ⇒ nunca notificada. */
  notificadoAt?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

/** Fila del historial. `ganadoresAnuales` es el conteo real de la colección winners de ese año. */
export interface RecapEditionSummaryDto {
  anio: number;
  nombre: string;
  alcance: string[];
  slides: number;
  ganadoresAnuales: number;
  /** activa | completada | borrador (derivado en backend). */
  estado: string;
}
